import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { handleUppercaseInput } from "@/lib/utils";
import { API, BACKEND_URL, apiFetch } from "@/lib/api";
import { useGarageBranding } from "@/hooks/useGarageBranding";
import { exportRowsToCSV } from "@/lib/csvExport";
import { toLocalISODate } from "@/lib/dateUtils";
import { useAuth } from "@/context/AuthContext";
import { statusConfig, issuePriorityConfig, issueStatusConfig, issueCategoryConfig } from "@/lib/uiConfig";
import DTCManagementSection from "@/components/DTCManagementSection";
import WorkHoldManager from "@/components/WorkHoldManager";
import TimeTracker from "@/components/TimeTracker";
import JobCardStatusWorkflow from "@/components/JobCardStatusWorkflow";
import AddVehicleDataForm from "@/components/forms/AddVehicleDataForm";
import SimpleAddPartForm from "@/components/forms/SimpleAddPartForm";
import MasterPartForm from "@/components/forms/MasterPartForm";
import { useFormDraft, DraftRestoredBar } from "@/components/FormDraft";
import VehicleCascadeDropdowns from "@/components/VehicleCascadeDropdowns";
import {
  Plus, Car, Wrench, AlertTriangle, CheckCircle, Clock, Package,
  Search, Bell, Settings, Users, Calendar, FileText, BarChart3,
  Home, Archive, DollarSign, Phone, Mail, MapPin, Menu, X,
  AlertCircle, Bug, Zap, Settings2, Gauge, Cog, Calculator,
  Eye, Download, Camera, Edit, Trash, RefreshCw, LogOut, LogIn,
  UserPlus, Shield, PackageX, Trash2, Play, List, Clipboard, Database, Minus,
  Check, CreditCard, Truck, Send, Code, FileCheck, Activity,
  TrendingUp, TrendingDown, ArrowUpCircle, ArrowDownCircle, Loader2,
  Filter, Info, ClipboardList, ChevronDown
} from "lucide-react";

import { AddServiceForm, AddPartToPurchaseOrderForm, AddExistingItemsToInvoiceForm } from "@/pages/shared/ItemForms";


const InvoiceItemRow = ({ item, onUpdate, onRemove }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editQuantity, setEditQuantity] = useState(item.quantity);
  const [editUnitPrice, setEditUnitPrice] = useState(item.unit_price);

  const handleSave = () => {
    if (editQuantity <= 0 || editUnitPrice < 0) {
      toast.error("Please enter valid quantity and price");
      return;
    }
    
    onUpdate(item.id, {
      quantity: editQuantity,
      unit_price: editUnitPrice
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditQuantity(item.quantity);
    setEditUnitPrice(item.unit_price);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex-1">
          <div className="font-medium text-sm mb-2">{item.name}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor={`edit-qty-${item.id}`} className="text-xs">Quantity</Label>
              <Input
                id={`edit-qty-${item.id}`}
                type="number"
                min="0.01"
                step="0.01"
                value={editQuantity}
                onChange={(e) => setEditQuantity(parseFloat(e.target.value) || 1)}
                className="mt-1 text-sm"
              />
            </div>
            <div>
              <Label htmlFor={`edit-price-${item.id}`} className="text-xs">Unit Price (₹)</Label>
              <Input
                id={`edit-price-${item.id}`}
                type="number"
                min="0"
                step="0.01"
                value={editUnitPrice}
                onChange={(e) => setEditUnitPrice(parseFloat(e.target.value) || 0)}
                className="mt-1 text-sm"
              />
            </div>
          </div>
          <div className="text-xs text-gray-600 mt-2">
            {item.type === 'service' ? '🔧' : '🔩'} {item.type.charAt(0).toUpperCase() + item.type.slice(1)} • 
            Total: ₹{(editQuantity * editUnitPrice).toFixed(2)}
          </div>
        </div>
        <div className="flex items-center space-x-2 ml-4">
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            Save
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="flex-1">
        <div className="font-medium text-sm">{item.name}</div>
        <div className="text-xs text-gray-600">
          {item.type === 'service' ? '🔧' : '🔩'} {item.type.charAt(0).toUpperCase() + item.type.slice(1)} • 
          Qty: {item.quantity} • Rate: ₹{item.unit_price}
        </div>
      </div>
      <div className="flex items-center space-x-3">
        <div className="text-sm font-medium">₹{item.total.toFixed(2)}</div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setIsEditing(true)}
          className="text-blue-600 hover:text-blue-700"
        >
          <Edit className="h-3 w-3" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onRemove}
          className="text-red-600 hover:text-red-700"
        >
          <Trash className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};

// Quotation Item Row component (similar to InvoiceItemRow)

const AddInvoiceForm = ({ onInvoiceAdded, customers, vehicles, jobCards, parts, services }) => {
  const [invoiceFormData, setInvoiceFormData] = useState({
    customer_id: "",
    customer_name: "",
    customer_phone: "",
    customer_address: "",
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: "",
    job_card_id: "",
    vehicle_info: "",
    notes: ""
  });

  const [vehicleDetails, setVehicleDetails] = useState({
    vin: "",
    make: "",
    model: "",
    series: "",
    year: "",
    mileage: "",
    license_plate: ""
  });

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [isVehicleAutoPopulated, setIsVehicleAutoPopulated] = useState(false);
  const [allowVehicleEdit, setAllowVehicleEdit] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = React.useRef(null);
  const [searchKey, setSearchKey] = React.useState(0);

  const [invoiceItems, setInvoiceItems] = useState([]);
  const [selectedItemType, setSelectedItemType] = useState("service");
  const [selectedItemId, setSelectedItemId] = useState("");
  const [itemQuantity, setItemQuantity] = useState(1);
  const [invoiceItemSearchTerm, setInvoiceItemSearchTerm] = useState("");
  const [gstEnabled, setGstEnabled] = useState(true);

  // Pick up the garage's default GST on/off preference
  useEffect(() => {
    const loadGstDefault = async () => {
      try {
        const res = await apiFetch(`${API}/settings/gst`);
        if (res.ok) {
          const data = await res.json();
          setGstEnabled(data.gst_enabled_default !== false);
        }
      } catch (e) { /* keep default true */ }
    };
    loadGstDefault();
  }, []);
  
  // Dialog states for Add New / Add Existing
  const [showAddExistingServiceDialog, setShowAddExistingServiceDialog] = useState(false);
  const [showAddNewServiceDialog, setShowAddNewServiceDialog] = useState(false);
  const [showAddExistingPartDialog, setShowAddExistingPartDialog] = useState(false);
  const [showAddNewPartDialog, setShowAddNewPartDialog] = useState(false);
  const [invoiceServiceSearchTerm, setInvoiceServiceSearchTerm] = useState("");
  const [invoicePartSearchTerm, setInvoicePartSearchTerm] = useState("");
  const [jobCardPopoverOpen, setJobCardPopoverOpen] = useState(false);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Autosave draft (create only)
  const invDraftHasContent =
    invoiceItems.length > 0 ||
    (invoiceFormData.customer_name || '').trim() !== '' ||
    (invoiceFormData.customer_phone || '').trim() !== '' ||
    (invoiceFormData.notes || '').trim() !== '' ||
    (invoiceFormData.job_card_id || '') !== '';
  const { draftRestored, clearDraft, dismissBanner } = useFormDraft({
    storageKey: 'draft:invoice-create',
    hasContent: invDraftHasContent,
    data: { invoiceFormData, vehicleDetails, invoiceItems },
    onRestore: (d) => {
      if (d.invoiceFormData) setInvoiceFormData(d.invoiceFormData);
      if (d.vehicleDetails) setVehicleDetails(d.vehicleDetails);
      if (Array.isArray(d.invoiceItems)) setInvoiceItems(d.invoiceItems);
    },
  });
  const discardInvoiceDraft = () => {
    clearDraft();
    setInvoiceFormData({
      customer_id: "", customer_name: "", customer_phone: "", customer_address: "",
      invoice_date: new Date().toISOString().split('T')[0], due_date: "",
      job_card_id: "", vehicle_info: "", notes: ""
    });
    setVehicleDetails({ vin: "", make: "", model: "", series: "", year: "", mileage: "", license_plate: "" });
    setInvoiceItems([]);
    setSelectedCustomer(null);
  };
  
  // Vehicle filter states for Services & Parts selection
  const [vehicleFilterMake, setVehicleFilterMake] = useState('all');
  const [vehicleFilterModel, setVehicleFilterModel] = useState('all');
  const [vehicleFilterSeries, setVehicleFilterSeries] = useState('all');
  
  // Vehicle data for filters
  const [vehicleData, setVehicleData] = useState({});
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableSeries, setAvailableSeries] = useState([]);

  // Calculate totals (GST removed)
  const subtotal = invoiceItems.reduce((sum, item) => sum + item.total, 0);
  const totalAmount = subtotal;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInvoiceFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setSelectedCustomer(customer);
      
      // Get vehicles for this customer from the separate vehicles array
      const customerVehicles = vehicles.filter(vehicle => vehicle && vehicle.id && vehicle.customer_id === customerId);
      setAvailableVehicles(customerVehicles);
      
      setInvoiceFormData(prev => ({
        ...prev,
        customer_id: customerId,
        customer_name: customer.name,
        customer_phone: customer.phone || customer.contact,
        customer_address: customer.address || ""
      }));

      // Auto-populate vehicle details from customer's primary vehicle
      if (customerVehicles.length > 0) {
        const primaryVehicle = customerVehicles[0]; // Use first vehicle as primary
        populateVehicleDetails(primaryVehicle);
        setIsVehicleAutoPopulated(true);
        setAllowVehicleEdit(false);
      } else {
        clearVehicleDetails();
        setIsVehicleAutoPopulated(false);
        setAllowVehicleEdit(true);
      }
    }
  };

  const handleVinSearch = (selectedVin) => {
    const vehicle = vehicles.find(v => v.vin === selectedVin);
    if (vehicle) {
      // Find and populate customer information
      const customer = customers.find(c => c.id === vehicle.customer_id);
      if (customer) {
        setSelectedCustomer(customer);
        setInvoiceFormData(prev => ({
          ...prev,
          customer_id: customer.id,
          customer_name: customer.name,
          customer_phone: customer.phone || customer.contact,
          customer_address: customer.address || ""
        }));
      }
      
      populateVehicleDetails(vehicle);
      setIsVehicleAutoPopulated(true);
      setAllowVehicleEdit(false);
    }
  };

  const populateVehicleDetails = (vehicle) => {
    setVehicleDetails({
      vin: vehicle.vin || "",
      make: vehicle.make || "",
      model: vehicle.model || "",
      series: vehicle.series || "",
      year: vehicle.year || "",
      mileage: vehicle.mileage || "",
      license_plate: vehicle.license_plate || ""
    });

    setInvoiceFormData(prev => ({
      ...prev,
      vehicle_info: `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''} ${vehicle.series ? `(${vehicle.series})` : ''} (${vehicle.license_plate || ''})`
    }));
  };

  const clearVehicleDetails = () => {
    setVehicleDetails({
      vin: "",
      make: "",
      model: "",
      series: "",
      year: "",
      mileage: "",
      license_plate: ""
    });
    setInvoiceFormData(prev => ({
      ...prev,
      vehicle_info: ""
    }));
  };

  const handleVehicleSelection = (vehicleId) => {
    // Lookup by id first; fall back to numeric index for backward compatibility
    let selectedVehicle = availableVehicles.find(v => v && v.id === vehicleId);
    if (!selectedVehicle) {
      const idx = parseInt(vehicleId);
      if (!isNaN(idx)) selectedVehicle = availableVehicles[idx];
    }
    if (selectedVehicle) {
      populateVehicleDetails(selectedVehicle);
      setIsVehicleAutoPopulated(true);
      setAllowVehicleEdit(false);
    }
  };

  const handleJobCardChange = (jobCardId) => {
    const jobCard = jobCards.find(jc => jc.id === jobCardId);
    if (jobCard) {
      setInvoiceFormData(prev => ({
        ...prev,
        job_card_id: jobCardId,
        notes: `Linked to Job Card: ${jobCard.job_number} - ${jobCard.title}`
      }));

      // If job card has vehicle information, populate it
      if (jobCard.vehicle_make && jobCard.vehicle_model) {
        const vehicleInfo = `${jobCard.year || ''} ${jobCard.vehicle_make} ${jobCard.vehicle_model} (${jobCard.vehicle_reg || ''})`;
        setInvoiceFormData(prev => ({
          ...prev,
          vehicle_info: vehicleInfo
        }));

        setVehicleDetails({
          vin: jobCard.vin || "",
          make: jobCard.vehicle_make || "",
          model: jobCard.vehicle_model || "",
          year: jobCard.year || "",
          mileage: jobCard.mileage || "",
          license_plate: jobCard.vehicle_reg || ""
        });
      }
    }
  };

  const addInvoiceItem = () => {
    if (!selectedItemId || itemQuantity <= 0) {
      toast.error("Please select an item and specify quantity");
      return;
    }

    let selectedItem;
    if (selectedItemType === "service") {
      selectedItem = services.find(s => s.id === selectedItemId);
    } else if (selectedItemType === "part") {
      selectedItem = parts.find(p => p.id === selectedItemId);
    }

    if (selectedItem) {
      const newItem = {
        id: Date.now(),
        type: selectedItemType,
        item_id: selectedItemId,
        name: selectedItem.name || selectedItem.part_name || selectedItem.description,
        description: selectedItem.description || selectedItem.name || selectedItem.part_name,
        quantity: itemQuantity,
        unit_price: selectedItem.price || selectedItem.total_price || selectedItem.selling_price || selectedItem.cost_price || 0,
        total: itemQuantity * (selectedItem.price || selectedItem.total_price || selectedItem.selling_price || selectedItem.cost_price || 0)
      };

      setInvoiceItems(prev => [...prev, newItem]);

      // Reset selection
      setSelectedItemId("");
      setItemQuantity(1);
    }
  };

  const removeInvoiceItem = (itemId) => {
    setInvoiceItems(prev => prev.filter(item => item.id !== itemId));
  };

  const updateInvoiceItem = (itemId, updatedItem) => {
    setInvoiceItems(prev => prev.map(item => 
      item.id === itemId ? {
        ...item,
        ...updatedItem,
        total: updatedItem.quantity * updatedItem.unit_price
      } : item
    ));
  };

  // Use ref-based filtering to survive Dialog remounts
  const getSearchTerm = () => {
    const el = document.querySelector('[data-testid="customer-search-input"]');
    return el ? el.value : '';
  };
  
  const filteredCustomers = React.useMemo(() => {
    const term = getSearchTerm().toLowerCase();
    if (!term) return customers;
    return customers.filter(customer => {
      const name = (customer.name || '').toLowerCase();
      const phone = String(customer.phone || '');
      const contact = String(customer.contact || '');
      return name.includes(term) || phone.includes(term) || contact.includes(term);
    });
  }, [customers, searchKey]);
  
  // Fetch vehicle data for item filtering (from vehicle_data collection)
  useEffect(() => {
    const fetchVehicleMakes = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-data/makes`);
        const makes = (response.data.makes || []).filter(make => make && make.trim() !== '');
        setAvailableMakes(makes.sort());
      } catch (error) {
        console.error('Error fetching vehicle makes:', error);
        setAvailableMakes([]);
      }
    };
    fetchVehicleMakes();
  }, []);
  
  // Update available models when make filter changes
  useEffect(() => {
    if (vehicleFilterMake === 'all') {
      setAvailableModels([]);
      setVehicleFilterModel('all');
      setAvailableSeries([]);
      setVehicleFilterSeries('all');
    } else {
      const fetchModels = async () => {
        try {
          const response = await axios.get(`${API}/vehicle-data/models/${encodeURIComponent(vehicleFilterMake)}`);
          const models = (response.data.models || []).filter(m => m && m.trim() !== '');
          setAvailableModels(models.sort());
        } catch (error) {
          setAvailableModels([]);
        }
      };
      fetchModels();
      setVehicleFilterModel('all');
      setAvailableSeries([]);
      setVehicleFilterSeries('all');
    }
  }, [vehicleFilterMake]);
  
  // Update available series when model filter changes
  useEffect(() => {
    if (vehicleFilterModel === 'all') {
      setAvailableSeries([]);
      setVehicleFilterSeries('all');
    } else {
      const fetchSeries = async () => {
        try {
          const response = await axios.get(`${API}/vehicle-data/series/${encodeURIComponent(vehicleFilterMake)}/${encodeURIComponent(vehicleFilterModel)}`);
          const series = (response.data.series || []).filter(s => s && s.trim() !== '');
          setAvailableSeries(series.sort());
        } catch (error) {
          setAvailableSeries([]);
        }
      };
      fetchSeries();
      setVehicleFilterSeries('all');
    }
  }, [vehicleFilterModel, vehicleFilterMake]);

  const getAvailableItems = () => {
    let items = [];
    
    if (selectedItemType === "service") {
      items = services || [];
    } else if (selectedItemType === "part") {
      items = parts || [];
    }
    
    // Apply vehicle compatibility filters if any are selected
    if (vehicleFilterMake !== 'all' || vehicleFilterModel !== 'all' || vehicleFilterSeries !== 'all') {
      items = items.filter(item => {
        // If item has no compatibility data, show it in all filters (backward compatibility)
        if (!item.compatible_makes || item.compatible_makes.length === 0) {
          return true;
        }
        
        // Check make compatibility
        const makeMatches = vehicleFilterMake === 'all' || 
          item.compatible_makes.includes(vehicleFilterMake);
        
        // Check model compatibility  
        const modelMatches = vehicleFilterModel === 'all' || 
          !item.compatible_models || 
          item.compatible_models.length === 0 || 
          item.compatible_models.includes(vehicleFilterModel);
        
        // Check series compatibility
        const seriesMatches = vehicleFilterSeries === 'all' || 
          !item.compatible_series || 
          item.compatible_series.length === 0 || 
          item.compatible_series.includes(vehicleFilterSeries);
        
        return makeMatches && modelMatches && seriesMatches;
      });
    }
    
    return items;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!invoiceFormData.customer_name.trim()) {
      toast.error("Please enter customer name");
      return;
    }

    if (invoiceItems.length === 0) {
      toast.error("Please add at least one service or part");
      return;
    }

    setIsSubmitting(true);

    try {
      // Construct vehicle_info from individual fields
      const constructedVehicleInfo = [
        vehicleDetails.year,
        vehicleDetails.make,
        vehicleDetails.model,
        vehicleDetails.series
      ].filter(Boolean).join(' ').trim();

      const invoiceData = {
        customer_id: invoiceFormData.customer_id,
        customer_name: invoiceFormData.customer_name,
        customer_phone: invoiceFormData.customer_phone,
        customer_address: invoiceFormData.customer_address,
        invoice_date: invoiceFormData.invoice_date,
        due_date: invoiceFormData.due_date || null,
        job_card_id: invoiceFormData.job_card_id || null,
        vehicle_info: constructedVehicleInfo || invoiceFormData.vehicle_info || 'Vehicle details not specified',
        // Add individual vehicle fields (coerce empty strings to null for backend Pydantic int parsing)
        vehicle_make: vehicleDetails.make || null,
        vehicle_model: vehicleDetails.model || null,
        vehicle_series: vehicleDetails.series || null,
        vehicle_year: vehicleDetails.year === '' || vehicleDetails.year === null || vehicleDetails.year === undefined
          ? null
          : (parseInt(vehicleDetails.year, 10) || null),
        vin: vehicleDetails.vin || null,
        vehicle_registration: vehicleDetails.license_plate || null,
        items: invoiceItems.map(item => ({
          description: item.name,
          quantity: item.quantity,
          rate: item.unit_price,
          amount: item.total,
          part_id: item.type === 'part' ? (item.item_id || null) : null,
          item_type: item.type || null,
          brand_id: item.type === 'part' ? (item.brand_id || null) : null,
          brand_name: item.type === 'part' ? (item.brand_name || null) : null,
          service_id: item.type === 'service' ? (item.item_id || null) : null
        })),
        notes: invoiceFormData.notes || "",
        gst_enabled: gstEnabled
      };

      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/invoices`, invoiceData);

      toast.success('Invoice created successfully!');
      clearDraft();
      onInvoiceAdded();

      // Reset form
      setInvoiceFormData({
        customer_id: "",
        customer_name: "",
        customer_phone: "",
        customer_address: "",
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: "",
        job_card_id: "",
        vehicle_info: "",
        notes: ""
      });
      
      setVehicleDetails({
        vin: "",
        make: "",
        model: "",
        year: "",
        mileage: "",
        license_plate: ""
      });

      setInvoiceItems([]);
      setSelectedCustomer(null);
      setAvailableVehicles([]);
      setSearchTerm('');
    } catch (error) {
      console.error('Error creating invoice:', error);
      
      // Show specific error message if available
      if (error.response && error.response.data) {
        if (error.response.data.detail) {
          // Handle Pydantic validation errors
          if (Array.isArray(error.response.data.detail)) {
            const errorMessages = error.response.data.detail.map(err => 
              `${err.loc.join('.')}: ${err.msg}`
            ).join(', ');
            toast.error(`Validation error: ${errorMessages}`);
          } else {
            toast.error(`Error: ${error.response.data.detail}`);
          }
        } else if (error.response.data.message) {
          toast.error(`Error: ${error.response.data.message}`);
        } else {
          toast.error('Failed to create invoice - invalid data format');
        }
      } else if (error.message) {
        toast.error(`Network error: ${error.message}`);
      } else {
        toast.error('Failed to create invoice - unknown error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Set due date 15 days from invoice date
  useEffect(() => {
    if (invoiceFormData.invoice_date) {
      const invoiceDate = new Date(invoiceFormData.invoice_date);
      const dueDate = new Date(invoiceDate.getTime() + 15 * 24 * 60 * 60 * 1000);
      setInvoiceFormData(prev => ({
        ...prev,
        due_date: dueDate.toISOString().split('T')[0]
      }));
    }
  }, [invoiceFormData.invoice_date]);

  return (
    <>
      <DialogHeader>
        <DialogTitle>Create New Invoice</DialogTitle>
      </DialogHeader>
      <div className="max-h-[85vh] overflow-y-auto pr-2">
        <form onSubmit={handleSubmit} className="space-y-6">
          {draftRestored && <DraftRestoredBar onDiscard={discardInvoiceDraft} onDismiss={dismissBanner} />}
          
          {/* Customer Information */}
          <div className="border-b border-gray-200 pb-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Information</h3>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="customer_search">Search Customers</Label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name or phone..."
                    className="w-full h-10 px-3 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="customer-search-input"
                    onInput={(e) => {
                      const val = e.target.value;
                      console.log('SEARCH INPUT:', val);
                      setSearchKey(prev => prev + 1);
                    }}
                  />
                  {getSearchTerm() && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1 h-7 w-7 p-0"
                      onClick={() => {
                        const el = document.querySelector('[data-testid="customer-search-input"]');
                        if (el) el.value = '';
                        setSearchKey(prev => prev + 1);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {getSearchTerm() && (
                  <div className="text-xs text-gray-600 mt-1">
                    Found {filteredCustomers.length} customer(s) matching "{getSearchTerm()}"
                  </div>
                )}
              </div>
              <div>
                <Label>Select Customer *</Label>
                <div className="border rounded-md max-h-48 overflow-y-auto bg-white">
                  {filteredCustomers.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500 text-center">
                      {getSearchTerm() ? `No customers found matching "${getSearchTerm()}"` : 'No customers available'}
                    </div>
                  ) : (
                    filteredCustomers.map(customer => {
                      const customerVehicles = vehicles.filter(v => v && v.customer_id === customer.id);
                      const licensePlates = customerVehicles.map(v => v.license_plate || v.reg_number || '').filter(Boolean).join(', ');
                      const isSelected = invoiceFormData.customer_id === customer.id;
                      return (
                        <div 
                          key={customer.id}
                          className={`px-3 py-2 cursor-pointer text-sm border-b last:border-b-0 transition-colors ${
                            isSelected 
                              ? 'bg-blue-50 text-blue-800 font-medium' 
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => handleCustomerChange(customer.id)}
                          data-testid={`customer-option-${customer.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{customer.name} {licensePlates ? `- ${licensePlates}` : ''}</span>
                            {customerVehicles.length > 0 && (
                              <span className="text-xs text-gray-400">({customerVehicles.length} vehicle{customerVehicles.length !== 1 ? 's' : ''})</span>
                            )}
                          </div>
                          {isSelected && <span className="text-xs text-blue-600">Selected</span>}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

              {availableVehicles.length > 1 && (
                <div>
                  <Label htmlFor="vehicle_select">Select Vehicle</Label>
                  <Select onValueChange={(value) => handleVehicleSelection(value)} data-testid="invoice-vehicle-select">
                    <SelectTrigger data-testid="invoice-vehicle-select-trigger">
                      <SelectValue placeholder="Choose customer vehicle" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableVehicles.map((vehicle, index) => (
                        <SelectItem
                          key={vehicle.id || `vehicle-${index}`}
                          value={vehicle.id || index.toString()}
                          data-testid={`invoice-vehicle-option-${index}`}
                        >
                          {vehicle.year} {vehicle.make} {vehicle.model} ({vehicle.license_plate})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label htmlFor="job_card_select">Link to Job Card (Optional)</Label>
                <Popover open={jobCardPopoverOpen} onOpenChange={setJobCardPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={jobCardPopoverOpen}
                      className="w-full justify-between font-normal"
                      data-testid="invoice-jobcard-select"
                    >
                      <span className="truncate text-left">
                        {invoiceFormData.job_card_id
                          ? (() => {
                              const jc = jobCards.find((j) => j.id === invoiceFormData.job_card_id);
                              return jc ? `${jc.job_number} - ${jc.vehicle_make} ${jc.vehicle_model}` : 'Choose job card';
                            })()
                          : 'Choose job card'}
                      </span>
                      <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search by job number, customer or vehicle..." data-testid="invoice-jobcard-search-input" />
                      <CommandList className="max-h-60">
                        <CommandEmpty>No job card found.</CommandEmpty>
                        <CommandGroup>
                          {jobCards.map(jobCard => (
                            <CommandItem
                              key={jobCard.id}
                              value={`${jobCard.job_number} ${jobCard.customer_name || ''} ${jobCard.vehicle_make || ''} ${jobCard.vehicle_model || ''} ${jobCard.vehicle_reg || ''}`}
                              onSelect={() => {
                                handleJobCardChange(jobCard.id);
                                setJobCardPopoverOpen(false);
                              }}
                              data-testid={`invoice-jobcard-option-${jobCard.id}`}
                            >
                              <Check className={`mr-2 h-4 w-4 ${invoiceFormData.job_card_id === jobCard.id ? 'opacity-100' : 'opacity-0'}`} />
                              {jobCard.job_number} - {jobCard.vehicle_make} {jobCard.vehicle_model}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label htmlFor="customer_name">Customer Name *</Label>
                <Input
                  id="customer_name"
                  name="customer_name"
                  value={invoiceFormData.customer_name}
                  onChange={handleInputChange}
                  required
                  placeholder="Customer full name"
                />
              </div>

              <div>
                <Label htmlFor="customer_phone">Phone Number</Label>
                <Input
                  id="customer_phone"
                  name="customer_phone"
                  value={invoiceFormData.customer_phone}
                  onChange={handleInputChange}
                  placeholder="+91 906173 9191"
                />
              </div>
            </div>

            <div className="mt-4">
              <div>
                <Label htmlFor="vehicle_info">Vehicle Information</Label>
                <Input
                  id="vehicle_info"
                  name="vehicle_info"
                  value={invoiceFormData.vehicle_info}
                  onChange={handleInputChange}
                  placeholder="2023 Toyota Corolla (ABC-123)"
                />
              </div>
            </div>

            <div className="mt-4">
              <Label htmlFor="customer_address">Customer Address</Label>
              <Textarea
                id="customer_address"
                name="customer_address"
                value={invoiceFormData.customer_address}
                onChange={handleInputChange}
                placeholder="Complete customer address"
                rows={2}
              />
            </div>

          {/* Vehicle Information */}
          <div className="border-b border-gray-200 pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Vehicle Information</h3>
              {selectedCustomer && availableVehicles.length > 0 && (
                <div className="flex items-center space-x-2">
                  <Badge className="bg-green-100 text-green-800 text-xs">
                    Auto-populated from customer
                  </Badge>
                  {isVehicleAutoPopulated && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setAllowVehicleEdit(!allowVehicleEdit)}
                      className="text-xs"
                    >
                      {allowVehicleEdit ? 'Lock' : 'Edit'} Vehicle Details
                    </Button>
                  )}
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="vin">VIN Number</Label>
                <Input
                  id="vin"
                  name="vin"
                  value={vehicleDetails.vin}
                  onChange={(e) => setVehicleDetails(prev => ({ ...prev, vin: e.target.value }))}
                  placeholder="1NXBR32E25Z123456"
                  disabled={isVehicleAutoPopulated && !allowVehicleEdit}
                />
              </div>

              <div>
                <Label htmlFor="license_plate">License Plate *</Label>
                <Input
                  id="license_plate"
                  name="license_plate"
                  value={vehicleDetails.license_plate}
                  onChange={(e) => setVehicleDetails(prev => ({ ...prev, license_plate: e.target.value }))}
                  placeholder="ABC-123"
                  disabled={isVehicleAutoPopulated && !allowVehicleEdit}
                />
              </div>

              <div>
                <Label htmlFor="mileage">Mileage (km)</Label>
                <Input
                  id="mileage"
                  name="mileage"
                  type="number"
                  value={vehicleDetails.mileage}
                  onChange={(e) => setVehicleDetails(prev => ({ ...prev, mileage: e.target.value }))}
                  placeholder="25000"
                  disabled={isVehicleAutoPopulated && !allowVehicleEdit}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
              <div>
                <Label htmlFor="make">Make *</Label>
                <Input
                  id="make"
                  name="make"
                  value={vehicleDetails.make}
                  onChange={(e) => setVehicleDetails(prev => ({ ...prev, make: e.target.value }))}
                  placeholder="Toyota, Honda, Maruti"
                  disabled={isVehicleAutoPopulated && !allowVehicleEdit}
                />
              </div>

              <div>
                <Label htmlFor="model">Model *</Label>
                <Input
                  id="model"
                  name="model"
                  value={vehicleDetails.model}
                  onChange={(e) => setVehicleDetails(prev => ({ ...prev, model: e.target.value }))}
                  placeholder="Corolla, City, Swift"
                  disabled={isVehicleAutoPopulated && !allowVehicleEdit}
                />
              </div>

              <div>
                <Label htmlFor="series">Series</Label>
                <Input
                  id="series"
                  name="series"
                  value={vehicleDetails.series}
                  onChange={(e) => setVehicleDetails(prev => ({ ...prev, series: e.target.value }))}
                  placeholder="VXI, ZXI, SX4"
                  disabled={isVehicleAutoPopulated && !allowVehicleEdit}
                />
                <p className="text-xs text-gray-500 mt-1">Optional: Trim level</p>
              </div>

              <div>
                <Label htmlFor="year">Year *</Label>
                <Input
                  id="year"
                  name="year"
                  type="number"
                  min="1980"
                  max="2030"
                  value={vehicleDetails.year}
                  onChange={(e) => setVehicleDetails(prev => ({ ...prev, year: e.target.value }))}
                  placeholder="2023"
                  disabled={isVehicleAutoPopulated && !allowVehicleEdit}
                />
              </div>
            </div>
          </div>

          {/* Services and Parts */}
          <div className="border-b border-gray-200 pb-4">
            <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
              <h3 className="text-lg font-medium text-gray-900">Services & Parts</h3>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={() => setShowAddNewServiceDialog(true)}
                  variant="outline"
                  size="sm"
                  className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                  data-testid="invoice-add-new-service-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add New Service
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowAddExistingServiceDialog(true)}
                  variant="outline"
                  size="sm"
                  data-testid="invoice-add-existing-service-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Existing Service
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowAddNewPartDialog(true)}
                  variant="outline"
                  size="sm"
                  className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                  data-testid="invoice-add-new-part-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add New Part
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowAddExistingPartDialog(true)}
                  variant="outline"
                  size="sm"
                  data-testid="invoice-add-existing-part-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Existing Part
                </Button>
              </div>
            </div>

            {/* Added Items */}
            <div className="space-y-2">
              {invoiceItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                  <Package className="h-8 w-8 mx-auto mb-2" />
                  <p>No items added yet</p>
                  <p className="text-sm">Click the buttons above to add services or parts</p>
                </div>
              ) : (
                invoiceItems.map(item => (
                  <InvoiceItemRow 
                    key={item.id} 
                    item={item} 
                    onUpdate={updateInvoiceItem} 
                    onRemove={() => removeInvoiceItem(item.id)} 
                  />
                ))
              )}
            </div>
          </div>

          {/* Invoice Details */}
          <div className="border-b border-gray-200 pb-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="invoice_date">Invoice Date</Label>
                <Input
                  id="invoice_date"
                  name="invoice_date"
                  type="date"
                  value={invoiceFormData.invoice_date}
                  onChange={handleInputChange}
                  data-testid="create-invoice-date-input"
                />
              </div>

              <div>
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  name="due_date"
                  type="date"
                  value={invoiceFormData.due_date}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="mt-4">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                value={invoiceFormData.notes}
                onChange={handleInputChange}
                placeholder="Any additional notes for the invoice"
                rows={3}
              />
            </div>
          </div>

          {/* Invoice Summary */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Invoice Summary</h4>

            <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-300">
              <div>
                <Label htmlFor="gst-toggle" className="font-medium">Apply GST</Label>
                <p className="text-xs text-gray-500">CGST+SGST or IGST will be calculated automatically and added to the total.</p>
              </div>
              <Switch id="gst-toggle" data-testid="invoice-gst-toggle" checked={gstEnabled} onCheckedChange={setGstEnabled} />
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal (Taxable Value):</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              {gstEnabled && (
                <div className="flex justify-between text-xs text-gray-500">
                  <span>GST:</span>
                  <span>Calculated on save (CGST+SGST or IGST based on customer state)</span>
                </div>
              )}
              <div className="flex justify-between text-base font-medium border-t border-gray-300 pt-2">
                <span>Total Amount{gstEnabled ? ' (excl. GST, shown after saving)' : ''}:</span>
                <span>₹{totalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="flex space-x-2">
            <Button 
              type="submit" 
              disabled={isSubmitting || invoiceItems.length === 0} 
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {isSubmitting ? "Creating Invoice..." : "Create Invoice"}
            </Button>
          </div>
        </form>
      </div>

      {/* Add New Service Dialog */}
      <Dialog open={showAddNewServiceDialog} onOpenChange={setShowAddNewServiceDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <AddServiceForm
            formData={{}}
            setFormData={() => {}}
            onServiceAdded={(service) => {
              const price = parseFloat(service.price) || 0;
              const newItem = {
                id: Date.now() + Math.random(),
                type: 'service',
                item_id: service.id,
                name: service.name,
                description: service.description || service.name,
                quantity: 1,
                unit_price: price,
                total: price
              };
              setInvoiceItems(prev => [...prev, newItem]);
              setShowAddNewServiceDialog(false);
              toast.success('Service added to invoice!');
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Add Existing Service Dialog */}
      <Dialog open={showAddExistingServiceDialog} onOpenChange={setShowAddExistingServiceDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <AddExistingItemsToInvoiceForm
            itemType="service"
            items={services}
            onItemsSelected={(selectedItems) => {
              const newItems = selectedItems.map(it => {
                const qty = it.quantity || 1;
                const price = parseFloat(it.unit_price) || 0;
                return {
                  id: Date.now() + Math.random(),
                  type: 'service',
                  item_id: it.item_id,
                  name: it.name,
                  description: it.description || it.name,
                  quantity: qty,
                  unit_price: price,
                  total: qty * price
                };
              });
              setInvoiceItems(prev => [...prev, ...newItems]);
              setShowAddExistingServiceDialog(false);
              toast.success(`${selectedItems.length} service(s) added to invoice!`);
            }}
            onCancel={() => setShowAddExistingServiceDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Add New Part Dialog */}
      <Dialog open={showAddNewPartDialog} onOpenChange={setShowAddNewPartDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <MasterPartForm
            onSuccess={(part) => {
              const nb = Array.isArray(part.brands) && part.brands.length > 0 ? part.brands[0] : null;
              const sellPrice = nb ? (parseFloat(nb.selling_price) || parseFloat(nb.cost_price) || 0) : (parseFloat(part.selling_price) || parseFloat(part.cost_price) || 0);
              const newItem = {
                id: Date.now() + Math.random(),
                type: 'part',
                item_id: part.id,
                name: part.part_name,
                description: part.description || part.part_name,
                quantity: 1,
                unit_price: sellPrice,
                total: sellPrice,
                brand_id: nb ? nb.id : null,
                brand_name: nb ? nb.brand_name : null
              };
              setInvoiceItems(prev => [...prev, newItem]);
              setShowAddNewPartDialog(false);
              toast.success('Part added to invoice!');
            }}
            onCancel={() => setShowAddNewPartDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Add Existing Part Dialog */}
      <Dialog open={showAddExistingPartDialog} onOpenChange={setShowAddExistingPartDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <AddExistingItemsToInvoiceForm
            itemType="part"
            items={parts}
            onItemsSelected={(selectedItems) => {
              const newItems = selectedItems.map(it => {
                const qty = it.quantity || 1;
                const price = parseFloat(it.unit_price) || 0;
                return {
                  id: Date.now() + Math.random(),
                  type: 'part',
                  item_id: it.item_id,
                  name: it.name,
                  description: it.description || it.name,
                  quantity: qty,
                  unit_price: price,
                  total: qty * price,
                  brand_id: it.brand_id || null,
                  brand_name: it.brand_name || null
                };
              });
              setInvoiceItems(prev => [...prev, ...newItems]);
              setShowAddExistingPartDialog(false);
              toast.success(`${selectedItems.length} part(s) added to invoice!`);
            }}
            onCancel={() => setShowAddExistingPartDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

// Add Service Form Component

const EditInvoiceForm = ({ invoice, onInvoiceUpdated, customers, jobCards, parts, services }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    status: invoice.status || 'draft',
    invoice_date: invoice.invoice_date ? invoice.invoice_date.split('T')[0] : '',
    due_date: invoice.due_date ? invoice.due_date.split('T')[0] : '',
    vehicle_info: invoice.vehicle_info || '',
    notes: invoice.notes || ''
  });

  const [invoiceItems, setInvoiceItems] = useState(
    invoice.items || []
  );

  const [newItem, setNewItem] = useState({
    description: '',
    quantity: 1,
    rate: 0,
    amount: 0
  });

  const [isAddingItem, setIsAddingItem] = useState(false);

  // 4-button dialog states (same as AddInvoiceForm)
  const [showAddExistingServiceDialog, setShowAddExistingServiceDialog] = useState(false);
  const [showAddNewServiceDialog, setShowAddNewServiceDialog] = useState(false);
  const [showAddExistingPartDialog, setShowAddExistingPartDialog] = useState(false);
  const [showAddNewPartDialog, setShowAddNewPartDialog] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddItem = () => {
    if (!newItem.description.trim()) {
      toast.error("Please enter item description");
      return;
    }

    const itemToAdd = {
      ...newItem,
      amount: newItem.quantity * newItem.rate,
      id: Date.now().toString() // Temporary ID
    };

    setInvoiceItems(prev => [...prev, itemToAdd]);
    setNewItem({ description: '', quantity: 1, rate: 0, amount: 0 });
    setIsAddingItem(false);
    toast.success("Item added successfully");
  };

  const handleRemoveItem = (index) => {
    setInvoiceItems(prev => prev.filter((_, i) => i !== index));
    toast.success("Item removed successfully");
  };

  const handleItemEdit = (index, field, value) => {
    setInvoiceItems(prev => prev.map((item, i) => {
      if (i === index) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'quantity' || field === 'rate') {
          updatedItem.amount = updatedItem.quantity * updatedItem.rate;
        }
        return updatedItem;
      }
      return item;
    }));
  };

  const calculateTotal = () => {
    return invoiceItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const updateData = {
        ...formData,
        invoice_date: formData.invoice_date || null,
        due_date: formData.due_date || null,
        items: invoiceItems,
        total_amount: calculateTotal()
      };

      const response = await axios.put(`${BACKEND_URL}/api/invoices/${invoice.id}`, updateData);
      
      toast.success('Invoice updated successfully!');
      onInvoiceUpdated(response.data);
    } catch (error) {
      console.error('Error updating invoice:', error);
      
      if (error.response && error.response.data) {
        if (error.response.data.detail) {
          if (Array.isArray(error.response.data.detail)) {
            const errorMessages = error.response.data.detail.map(err => 
              `${err.loc.join('.')}: ${err.msg}`
            ).join(', ');
            toast.error(`Validation error: ${errorMessages}`);
          } else {
            toast.error(`Error: ${error.response.data.detail}`);
          }
        } else {
          toast.error('Failed to update invoice - server error');
        }
      } else {
        toast.error('Failed to update invoice - network error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <div className="space-y-6 max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Edit Invoice #{invoice.invoice_number}</DialogTitle>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        {/* Invoice Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div>
            <Label htmlFor="status">Status *</Label>
            <Select name="status" value={formData.status} onValueChange={(value) => setFormData(prev => ({...prev, status: value}))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="edit_invoice_date">Invoice Date</Label>
            <Input
              id="edit_invoice_date"
              name="invoice_date"
              type="date"
              value={formData.invoice_date}
              onChange={handleInputChange}
              data-testid="edit-invoice-date-input"
            />
          </div>
          
          <div>
            <Label htmlFor="due_date">Due Date</Label>
            <Input
              id="due_date"
              name="due_date"
              type="date"
              value={formData.due_date}
              onChange={handleInputChange}
            />
          </div>

          <div>
            <Label>Total Amount</Label>
            <Input value={`₹${calculateTotal().toFixed(2)}`} disabled className="font-medium" />
          </div>
        </div>

        {/* Vehicle Information */}
        <div className="mb-6">
          <Label htmlFor="vehicle_info">Vehicle Information</Label>
          <Input
            id="vehicle_info"
            name="vehicle_info"
            value={formData.vehicle_info}
            onChange={handleInputChange}
            placeholder="e.g., 2023 Toyota Corolla (ABC-123)"
          />
        </div>

        {/* Invoice Items */}
        <div className="mb-6">
          <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
            <h3 className="text-lg font-medium">Invoice Items</h3>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={() => setShowAddNewServiceDialog(true)}
                variant="outline"
                size="sm"
                className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                data-testid="edit-invoice-add-new-service-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add New Service
              </Button>
              <Button
                type="button"
                onClick={() => setShowAddExistingServiceDialog(true)}
                variant="outline"
                size="sm"
                data-testid="edit-invoice-add-existing-service-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Existing Service
              </Button>
              <Button
                type="button"
                onClick={() => setShowAddNewPartDialog(true)}
                variant="outline"
                size="sm"
                className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                data-testid="edit-invoice-add-new-part-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add New Part
              </Button>
              <Button
                type="button"
                onClick={() => setShowAddExistingPartDialog(true)}
                variant="outline"
                size="sm"
                data-testid="edit-invoice-add-existing-part-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Existing Part
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsAddingItem(true)}
                className="text-gray-700"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Custom Item
              </Button>
            </div>
          </div>

          {/* Add New Custom Item Form (free-text fallback) */}
          {isAddingItem && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-4 bg-blue-50 rounded-lg mb-4">
              <Input
                placeholder="Item description"
                value={newItem.description}
                onChange={(e) => setNewItem(prev => ({...prev, description: e.target.value}))}
              />
              <Input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="Quantity"
                value={newItem.quantity}
                onChange={(e) => setNewItem(prev => ({...prev, quantity: parseFloat(e.target.value) || 1}))}
              />
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="Rate (₹)"
                value={newItem.rate}
                onChange={(e) => setNewItem(prev => ({...prev, rate: parseFloat(e.target.value) || 0}))}
              />
              <div className="flex space-x-2">
                <Button type="button" onClick={handleAddItem} className="bg-green-600 hover:bg-green-700">
                  Add
                </Button>
                <Button type="button" variant="outline" onClick={() => {setIsAddingItem(false); setNewItem({description: '', quantity: 1, rate: 0, amount: 0});}}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Items List */}
          <div className="space-y-2">
            {invoiceItems.map((item, index) => (
              <div key={item.id || `item-${index}`} className="grid grid-cols-1 md:grid-cols-5 gap-3 p-3 bg-gray-50 rounded-lg">
                <Input
                  value={item.description}
                  onChange={(e) => handleItemEdit(index, 'description', e.target.value)}
                  placeholder="Description"
                />
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={item.quantity}
                  onChange={(e) => handleItemEdit(index, 'quantity', parseFloat(e.target.value) || 1)}
                  placeholder="Qty"
                />
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.rate}
                  onChange={(e) => handleItemEdit(index, 'rate', parseFloat(e.target.value) || 0)}
                  placeholder="Rate (₹)"
                />
                <Input
                  value={`₹${item.amount?.toFixed(2) || '0.00'}`}
                  disabled
                  className="font-medium"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleRemoveItem(index)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
            ))}
            
            {invoiceItems.length === 0 && (
              <p className="text-gray-500 text-center py-4">No items added yet</p>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows={3}
            placeholder="Additional notes or comments for this invoice..."
          />
        </div>

        {/* Form Actions */}
        <div className="flex justify-between items-center pt-4 border-t">
          <div className="text-sm text-gray-600">
            Changes will be saved to the database
          </div>
          <div className="flex space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onInvoiceUpdated(invoice)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? 'Updating...' : 'Update Invoice'}
            </Button>
          </div>
        </div>
      </form>
    </div>

    {/* Add New Service Dialog */}
    <Dialog open={showAddNewServiceDialog} onOpenChange={setShowAddNewServiceDialog}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <AddServiceForm
          formData={{}}
          setFormData={() => {}}
          onServiceAdded={(service) => {
            const price = parseFloat(service.price) || 0;
            const newRow = {
              id: `service_${service.id}_${Date.now()}`,
              description: service.name,
              quantity: 1,
              rate: price,
              amount: price
            };
            setInvoiceItems(prev => [...prev, newRow]);
            setShowAddNewServiceDialog(false);
            toast.success('Service added to invoice!');
          }}
        />
      </DialogContent>
    </Dialog>

    {/* Add Existing Service Dialog */}
    <Dialog open={showAddExistingServiceDialog} onOpenChange={setShowAddExistingServiceDialog}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <AddExistingItemsToInvoiceForm
          itemType="service"
          items={services || []}
          targetLabel="invoice"
          onItemsSelected={(selectedItems) => {
            const newRows = selectedItems.map(it => {
              const qty = it.quantity || 1;
              const price = parseFloat(it.unit_price) || 0;
              return {
                id: `service_${it.item_id}_${Date.now()}_${Math.random()}`,
                description: it.name,
                quantity: qty,
                rate: price,
                amount: qty * price
              };
            });
            setInvoiceItems(prev => [...prev, ...newRows]);
            setShowAddExistingServiceDialog(false);
            toast.success(`${selectedItems.length} service(s) added to invoice!`);
          }}
          onCancel={() => setShowAddExistingServiceDialog(false)}
        />
      </DialogContent>
    </Dialog>

    {/* Add New Part Dialog */}
    <Dialog open={showAddNewPartDialog} onOpenChange={setShowAddNewPartDialog}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <MasterPartForm
          onSuccess={(part) => {
            const nb = Array.isArray(part.brands) && part.brands.length > 0 ? part.brands[0] : null;
            const sellPrice = nb ? (parseFloat(nb.selling_price) || parseFloat(nb.cost_price) || 0) : (parseFloat(part.selling_price) || parseFloat(part.cost_price) || 0);
            const newRow = {
              id: `part_${part.id}_${Date.now()}`,
              description: part.part_name,
              quantity: 1,
              rate: sellPrice,
              amount: sellPrice,
              part_id: part.id,
              item_type: 'part',
              brand_id: nb ? nb.id : null,
              brand_name: nb ? nb.brand_name : null
            };
            setInvoiceItems(prev => [...prev, newRow]);
            setShowAddNewPartDialog(false);
            toast.success('Part added to invoice!');
          }}
          onCancel={() => setShowAddNewPartDialog(false)}
        />
      </DialogContent>
    </Dialog>

    {/* Add Existing Part Dialog */}
    <Dialog open={showAddExistingPartDialog} onOpenChange={setShowAddExistingPartDialog}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <AddExistingItemsToInvoiceForm
          itemType="part"
          items={parts || []}
          targetLabel="invoice"
          onItemsSelected={(selectedItems) => {
            const newRows = selectedItems.map(it => {
              const qty = it.quantity || 1;
              const price = parseFloat(it.unit_price) || 0;
              return {
                id: `part_${it.item_id}_${Date.now()}_${Math.random()}`,
                description: it.name,
                quantity: qty,
                rate: price,
                amount: qty * price,
                part_id: it.item_id,
                item_type: 'part',
                brand_id: it.brand_id || null,
                brand_name: it.brand_name || null
              };
            });
            setInvoiceItems(prev => [...prev, ...newRows]);
            setShowAddExistingPartDialog(false);
            toast.success(`${selectedItems.length} part(s) added to invoice!`);
          }}
          onCancel={() => setShowAddExistingPartDialog(false)}
        />
      </DialogContent>
    </Dialog>
    </>
  );
};

// Invoice View Component

const InvoiceView = ({ invoice, onClose, customers, vehicles, jobCards }) => {
  const businessName = useGarageBranding();
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [loadingPaymentSummary, setLoadingPaymentSummary] = useState(true);
  const [hideGarageInfo, setHideGarageInfo] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundMethod, setRefundMethod] = useState('cash');
  const [refundBankAccountId, setRefundBankAccountId] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [submittingRefund, setSubmittingRefund] = useState(false);

  if (!invoice) return null;

  const loadPaymentSummary = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoice.id}/payment-summary`);
      setPaymentSummary(response.data);
    } catch (error) {
      console.error("Error fetching payment summary:", error);
    } finally {
      setLoadingPaymentSummary(false);
    }
  };

  // Fetch payment summary when component loads
  useEffect(() => {
    if (invoice.id) {
      loadPaymentSummary();
    }
  }, [invoice.id]);

  useEffect(() => {
    const loadBankAccounts = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/bank-accounts`);
        setBankAccounts(res.data || []);
      } catch (e) {
        console.error("Error loading bank accounts:", e);
      }
    };
    loadBankAccounts();
  }, []);

  const openRefundModal = () => {
    setRefundAmount((paymentSummary?.refund_due || 0).toString());
    setRefundMethod('cash');
    setRefundBankAccountId('');
    setShowRefundModal(true);
  };

  const handleRecordRefund = async (e) => {
    e.preventDefault();
    if (!refundAmount || parseFloat(refundAmount) <= 0) {
      toast.error('Enter a valid refund amount');
      return;
    }
    if (refundMethod === 'bank_account' && !refundBankAccountId) {
      toast.error('Please select a bank account');
      return;
    }
    setSubmittingRefund(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoice.id}/record-refund`, {
        amount: parseFloat(refundAmount),
        payment_method: refundMethod,
        bank_account_id: refundMethod === 'bank_account' ? refundBankAccountId : null,
      });
      toast.success('Refund recorded successfully');
      setShowRefundModal(false);
      loadPaymentSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record refund');
    } finally {
      setSubmittingRefund(false);
    }
  };

  // Get customer information from customers array using customer_id
  const customer = customers?.find(c => c.id === invoice.customer_id);
  
  // Get vehicle information from job card if available
  const jobCard = jobCards?.find(jc => jc.id === invoice.job_card_id);
  const vehicle = jobCard ? vehicles?.find(v => v.id === jobCard.vehicle_id) : null;
  
  // Debug logging (can be removed later)
  console.log('Invoice vehicle lookup:', {
    jobCardId: invoice.job_card_id,
    jobCard: jobCard,
    vehicleId: jobCard?.vehicle_id,
    vehicle: vehicle,
    vehicleInfo: invoice.vehicle_info
  });
  
  // Construct display information with better fallbacks
  const customerInfo = {
    name: invoice.customer_name || customer?.name || `Customer ID: ${invoice.customer_id || 'Unknown'}`,
    phone: invoice.customer_phone || customer?.contact || customer?.phone || 'Phone: Not Available',
    address: invoice.customer_address || customer?.location || customer?.address || 'Address: Not Available'
  };
  
  // Enhanced vehicle information logic
  let vehicleDisplayInfo = 'Vehicle: Not Available';
  
  // Debug logging
  console.log('Invoice vehicle info debug:', {
    invoice_vehicle_info: invoice.vehicle_info,
    vehicle_make: invoice.vehicle_make,
    vehicle_model: invoice.vehicle_model,
    vehicle_series: invoice.vehicle_series,
    vehicle_year: invoice.vehicle_year,
    full_invoice: invoice
  });
  
  // First try to construct from invoice's individual vehicle fields
  if (invoice.vehicle_make || invoice.vehicle_model || invoice.vehicle_year) {
    const parts = [
      invoice.vehicle_year,
      invoice.vehicle_make,
      invoice.vehicle_model,
      invoice.vehicle_series
    ].filter(part => part && String(part).trim()).map(part => String(part).trim());
    
    if (parts.length > 0) {
      vehicleDisplayInfo = parts.join(' ');
      console.log('Vehicle info constructed from individual fields:', vehicleDisplayInfo);
    }
  } else if (invoice.vehicle_info && invoice.vehicle_info.trim() && invoice.vehicle_info !== 'Vehicle details not specified') {
    // Use direct vehicle info if available
    vehicleDisplayInfo = invoice.vehicle_info;
    console.log('Vehicle info from direct field:', vehicleDisplayInfo);
  } else if (vehicle) {
    // Construct from vehicle object
    const parts = [
      vehicle.year,
      vehicle.make,
      vehicle.model,
      vehicle.series
    ].filter(part => part && String(part).trim()).map(part => String(part).trim()).join(' ');
    
    const licensePart = vehicle.license_plate ? ` (${vehicle.license_plate})` : '';
    vehicleDisplayInfo = parts ? `${parts}${licensePart}` : `Vehicle ID: ${vehicle.id}`;
    console.log('Vehicle info from vehicle object:', vehicleDisplayInfo);
  } else if (jobCard) {
    // Show job card info even if vehicle not found
    vehicleDisplayInfo = `Job Card Vehicle: ${jobCard.vehicle_make || ''} ${jobCard.vehicle_model || ''} ${jobCard.year || ''}`.trim() || `Vehicle from Job Card #${invoice.job_card_id}`;
    console.log('Vehicle info from job card:', vehicleDisplayInfo);
  } else if (invoice.job_card_id) {
    // Job card ID exists but job card not found
    vehicleDisplayInfo = `Job Card #${invoice.job_card_id} (Vehicle details not loaded)`;
    console.log('Vehicle info from job card ID only:', vehicleDisplayInfo);
  }
  
  console.log('Final vehicle display info:', vehicleDisplayInfo);
  
  const vehicleInfo = {
    info: vehicleDisplayInfo,
    vin: invoice.vin || vehicle?.vin || '',
    registration: invoice.vehicle_registration || vehicle?.license_plate || jobCard?.vehicle_reg || '',
    jobCard: invoice.job_card_id || null,
    debug: {
      hasVehicleInfo: !!invoice.vehicle_info,
      hasJobCard: !!jobCard,
      hasVehicle: !!vehicle,
      jobCardCount: jobCards?.length || 0,
      vehicleCount: vehicles?.length || 0
    }
  };

  const handlePrint = () => {
    // Create a new window for printing with only the invoice content
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoice.invoice_number}</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 0.5in;
          }
          
          body {
            font-family: Arial, sans-serif;
            font-size: 11px;
            line-height: 1.3;
            color: black;
            margin: 0;
            padding: 0;
            background: white;
          }
          
          .invoice-container {
            max-width: 100%;
            margin: 0;
            padding: 0;
          }
          
          .header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
            align-items: flex-start;
          }
          
          .header-left h1 {
            font-size: 24px;
            margin: 0 0 5px 0;
            font-weight: bold;
          }
          
          .header-left p {
            margin: 2px 0;
            font-size: 11px;
          }
          
          .header-right {
            text-align: right;
          }
          
          .header-right h2 {
            font-size: 20px;
            color: #2563eb;
            margin: 0 0 5px 0;
            font-weight: bold;
          }
          
          .header-right p {
            margin: 2px 0;
            font-size: 11px;
          }
          
          .section {
            margin-bottom: 15px;
          }
          
          .row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 15px;
          }
          
          .col {
            width: 48%;
          }
          
          .col h3 {
            font-size: 12px;
            font-weight: bold;
            margin: 0 0 5px 0;
          }
          
          .col p {
            margin: 2px 0;
            font-size: 11px;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0;
          }
          
          th, td {
            border: 1px solid #000;
            padding: 6px 8px;
            text-align: left;
            font-size: 10px;
          }
          
          th {
            background-color: #f0f0f0;
            font-weight: bold;
          }
          
          .text-center { text-align: center; }
          .text-right { text-align: right; }
          .font-bold { font-weight: bold; }
          
          .totals {
            margin: 15px 0;
            text-align: right;
          }
          
          .totals table {
            margin-left: auto;
            width: 300px;
          }
          
          .footer {
            margin-top: 20px;
            padding-top: 10px;
            border-top: 1px solid #ccc;
            text-align: center;
            font-size: 10px;
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <!-- Header -->
          <div class="header">
            <div class="header-left">
              ${hideGarageInfo ? '' : `<h1>${businessName.toUpperCase()}</h1>
              <p>Professional Automotive Services</p>`}
            </div>
            <div class="header-right">
              <h2>INVOICE</h2>
              <p><strong>#${invoice.invoice_number}</strong></p>
              <p>Date: ${new Date(invoice.invoice_date || invoice.created_at).toLocaleDateString('en-IN')}</p>
              ${invoice.due_date ? `<p>Due: ${new Date(invoice.due_date).toLocaleDateString('en-IN')}</p>` : ''}
            </div>
          </div>

          <!-- Customer and Vehicle Info -->
          <div class="row">
            <div class="col">
              <h3>Bill To:</h3>
              <p><strong>${customerInfo.name}</strong></p>
              <p>${customerInfo.phone}</p>
              <p>${customerInfo.address}</p>
            </div>
            <div class="col">
              <h3>Vehicle Information:</h3>
              <p>${vehicleInfo.info}</p>
              ${vehicleInfo.registration ? `<p>Reg. No: ${vehicleInfo.registration}</p>` : ''}
              ${vehicleInfo.vin ? `<p>VIN: ${vehicleInfo.vin}</p>` : ''}
              ${vehicleInfo.jobCard ? `<p>Job Card: #${vehicleInfo.jobCard}</p>` : ''}
            </div>
          </div>

          <!-- Items Table -->
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th class="text-center" style="width: 10%">HSN/SAC</th>
                <th class="text-center" style="width: 8%">Qty</th>
                <th class="text-right" style="width: 13%">Rate (₹)</th>
                <th class="text-right" style="width: 13%">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              ${invoice.items && invoice.items.length > 0 ? 
                invoice.items.map(item => `
                  <tr>
                    <td>${item.description}</td>
                    <td class="text-center">${item.hsn_sac_code || '-'}</td>
                    <td class="text-center">${item.quantity || 1}</td>
                    <td class="text-right">₹${(item.rate || 0).toLocaleString('en-IN')}</td>
                    <td class="text-right font-bold">₹${(item.amount || 0).toLocaleString('en-IN')}</td>
                  </tr>
                `).join('') : 
                '<tr><td colspan="5" class="text-center" style="color: #666;">No items found</td></tr>'
              }
            </tbody>
          </table>

          <!-- Totals -->
          <div class="totals">
            <table>
              <tr>
                <td><strong>Subtotal (Taxable Value):</strong></td>
                <td class="text-right"><strong>₹${subtotal.toLocaleString('en-IN')}</strong></td>
              </tr>
              ${invoice.gst_enabled && invoice.total_tax > 0 ? `
              ${invoice.is_interstate ? `
              <tr>
                <td>IGST:</td>
                <td class="text-right">₹${(invoice.total_igst || 0).toLocaleString('en-IN')}</td>
              </tr>` : `
              <tr>
                <td>CGST:</td>
                <td class="text-right">₹${(invoice.total_cgst || 0).toLocaleString('en-IN')}</td>
              </tr>
              <tr>
                <td>SGST:</td>
                <td class="text-right">₹${(invoice.total_sgst || 0).toLocaleString('en-IN')}</td>
              </tr>`}
              ` : ''}
              <tr style="background-color: #f0f0f0;">
                <td><strong>Total Amount:</strong></td>
                <td class="text-right" style="color: #2563eb;"><strong>₹${total.toLocaleString('en-IN')}</strong></td>
              </tr>
              ${Number(invoice.discount_amount || 0) > 0 ? `
              <tr>
                <td style="color: #b45309;">Discount${invoice.discount_reason ? ` (${invoice.discount_reason})` : ''}:</td>
                <td class="text-right" style="color: #b45309;">− ₹${Number(invoice.discount_amount || 0).toLocaleString('en-IN')}</td>
              </tr>
              <tr>
                <td><strong>Net Payable:</strong></td>
                <td class="text-right"><strong>₹${Math.max(0, total - Number(invoice.discount_amount || 0)).toLocaleString('en-IN')}</strong></td>
              </tr>
              ` : ''}
              ${Number(invoice.amount_paid || 0) > 0 ? `
              <tr>
                <td>Amount Paid:</td>
                <td class="text-right" style="color: #16a34a;">₹${Number(invoice.amount_paid || 0).toLocaleString('en-IN')}</td>
              </tr>
              <tr>
                <td><strong>Balance Due:</strong></td>
                <td class="text-right" style="color: #ea580c;"><strong>₹${Math.max(0, total - Number(invoice.discount_amount || 0) - Number(invoice.amount_paid || 0)).toLocaleString('en-IN')}</strong></td>
              </tr>
              ` : ''}
            </table>
          </div>

          <!-- Notes -->
          ${invoice.notes ? `
            <div class="section">
              <h3>Notes:</h3>
              <p style="background-color: #f9f9f9; padding: 8px; border: 1px solid #ddd;">${invoice.notes}</p>
            </div>
          ` : ''}

          <!-- Status -->
          <div class="section">
            <p><strong>Status:</strong> ${invoice.status ? invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1) : 'Draft'}</p>
          </div>

          <!-- Footer -->
          ${hideGarageInfo ? '' : `
          <div class="footer">
            <p><strong>Thank you for choosing our Garage for your automotive needs!</strong></p>
          </div>
          `}
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = function() {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }, 250);
    };
  };

  // Calculate totals
  const subtotal = invoice.items?.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0) || 0;
  const total = Number(invoice.total_amount) || subtotal; // total_amount already includes GST when gst_enabled

  return (
    <div className="invoice-view">
      {/* Print Styles - Simple and Reliable */}
      <style jsx>{`
        @media print {
          /* Page setup for single page */
          @page {
            size: A4 portrait;
            margin: 0.4in;
          }
          
          /* Hide everything by default */
          body * {
            visibility: hidden;
          }
          
          /* Show only the invoice content */
          .invoice-view,
          .invoice-view * {
            visibility: visible;
          }
          
          /* Position invoice view properly */
          .invoice-view {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            height: auto !important;
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            font-size: 11px !important;
          }
          
          /* Hide non-printable elements */
          .no-print {
            display: none !important;
            visibility: hidden !important;
          }
          
          /* Compact the content for single page */
          .invoice-view .print-content {
            padding: 10px !important;
            font-size: 11px !important;
            line-height: 1.2 !important;
          }
          
          .invoice-view h1 {
            font-size: 18px !important;
            margin: 0 0 6px 0 !important;
          }
          
          .invoice-view h2 {
            font-size: 15px !important;
            margin: 0 0 4px 0 !important;
          }
          
          .invoice-view h3 {
            font-size: 12px !important;
            margin: 6px 0 3px 0 !important;
          }
          
          .invoice-view .mb-8,
          .invoice-view .mb-6 {
            margin-bottom: 8px !important;
          }
          
          .invoice-view .mb-4 {
            margin-bottom: 6px !important;
          }
          
          .invoice-view table {
            font-size: 10px !important;
            margin: 6px 0 !important;
          }
          
          .invoice-view th,
          .invoice-view td {
            padding: 4px 6px !important;
            font-size: 10px !important;
            line-height: 1.1 !important;
          }
          
          .invoice-view .p-8 {
            padding: 10px !important;
          }
          
          .invoice-view .py-4 {
            padding-top: 3px !important;
            padding-bottom: 3px !important;
          }
          
          .invoice-view .py-3 {
            padding-top: 2px !important;
            padding-bottom: 2px !important;
          }
          
          .invoice-view .pt-4 {
            padding-top: 6px !important;
          }
        }
      `}</style>

      {/* Sticky Header */}
      <div className="sticky top-0 bg-white z-10 border-b border-gray-200 p-4 no-print">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Invoice #{invoice.invoice_number}</h3>
          <Button onClick={onClose} variant="outline" size="sm">
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="overflow-y-auto max-h-[calc(90vh-160px)] p-1 invoice-content">
        {/* Regular screen content */}
        <div className="bg-white p-8 max-w-4xl mx-auto print-content">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            {!hideGarageInfo && (<>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{businessName.toUpperCase()}</h1>
            <p className="text-gray-600">Professional Automotive Services</p>
            </>)}
          </div>
          
          <div className="text-right">
            <h2 className="text-2xl font-bold text-blue-600 mb-2">INVOICE</h2>
            <p className="text-lg font-semibold">#{invoice.invoice_number}</p>
            <p className="text-sm text-gray-600">
              Date: {new Date(invoice.invoice_date || invoice.created_at).toLocaleDateString('en-IN')}
            </p>
            {invoice.due_date && (
              <p className="text-sm text-gray-600">
                Due: {new Date(invoice.due_date).toLocaleDateString('en-IN')}
              </p>
            )}
          </div>
        </div>

        {/* Customer Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Bill To:</h3>
            <div className="text-gray-700">
              <p className="font-medium text-lg">{customerInfo.name}</p>
              <p className="text-sm">Phone: {customerInfo.phone}</p>
              <p className="text-sm mt-1">{customerInfo.address}</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Vehicle Information:</h3>
            <div className="text-gray-700">
              <p className="text-sm">{vehicleInfo.info}</p>
              {vehicleInfo.registration && (
                <p className="text-sm" data-testid="invoice-view-vehicle-reg">Reg. No: {vehicleInfo.registration}</p>
              )}
              {vehicleInfo.vin && (
                <p className="text-sm" data-testid="invoice-view-vin">VIN: {vehicleInfo.vin}</p>
              )}
              {vehicleInfo.jobCard && (
                <p className="text-sm">Job Card: #{vehicleInfo.jobCard}</p>
              )}
            </div>
          </div>
        </div>

        {/* Invoice Items Table */}
        <div className="mb-8">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Description</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">HSN/SAC</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900">Qty</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-900">Rate (₹)</th>
                <th className="text-right py-3 px-4 font-semibold text-gray-900">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items && invoice.items.length > 0 ? (
                invoice.items.map((item, index) => (
                  <tr key={item.id || `invoice-item-${index}`} className="border-b border-gray-200">
                    <td className="py-4 px-4 text-gray-800">{item.description}</td>
                    <td className="py-4 px-4 text-center text-gray-600" data-testid={`invoice-item-hsn-${index}`}>{item.hsn_sac_code || '-'}</td>
                    <td className="py-4 px-4 text-center text-gray-800">{item.quantity || 1}</td>
                    <td className="py-4 px-4 text-right text-gray-800">
                      ₹{(item.rate || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="py-4 px-4 text-right text-gray-800 font-medium">
                      ₹{(item.amount || 0).toLocaleString('en-IN')}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="py-4 px-4 text-center text-gray-500">
                    No items found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex justify-end mb-8 totals-section">
          <div className="w-64">
            <div className="flex justify-between py-2 border-t border-gray-300">
              <span className="font-semibold text-gray-900">Subtotal (Taxable Value):</span>
              <span className="font-semibold text-gray-900">₹{subtotal.toLocaleString('en-IN')}</span>
            </div>
            {invoice.gst_enabled && invoice.total_tax > 0 && (
              invoice.is_interstate ? (
                <div className="flex justify-between py-1 text-sm text-gray-700" data-testid="invoice-igst-row">
                  <span>IGST:</span>
                  <span>₹{(invoice.total_igst || 0).toLocaleString('en-IN')}</span>
                </div>
              ) : (
                <>
                  <div className="flex justify-between py-1 text-sm text-gray-700" data-testid="invoice-cgst-row">
                    <span>CGST:</span>
                    <span>₹{(invoice.total_cgst || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between py-1 text-sm text-gray-700" data-testid="invoice-sgst-row">
                    <span>SGST:</span>
                    <span>₹{(invoice.total_sgst || 0).toLocaleString('en-IN')}</span>
                  </div>
                </>
              )
            )}
            <div className="flex justify-between py-3 border-t-2 border-gray-900 bg-gray-50 px-4">
              <span className="text-lg font-bold text-gray-900">Total Amount:</span>
              <span className="text-lg font-bold text-blue-600">₹{total.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>

        {/* Overpaid — Refund Due banner (always visible when overpaid, regardless of how the
            overpayment happened — advance payment, quotation advance, or a regular payment) */}
        {!loadingPaymentSummary && paymentSummary && paymentSummary.is_overpaid && (
          <div className="mb-6 border-2 border-purple-300 rounded-lg bg-purple-50 p-5 flex items-center justify-between flex-wrap gap-3" data-testid="overpaid-refund-banner">
            <div>
              <div className="text-sm font-semibold text-purple-700">Overpaid — Refund Due</div>
              <div className="text-2xl font-bold text-purple-800" data-testid="overpaid-refund-amount">
                ₹{(paymentSummary.refund_due || 0).toLocaleString('en-IN')}
              </div>
              {paymentSummary.total_refunded > 0 && (
                <div className="text-xs text-purple-600 mt-1">
                  ₹{paymentSummary.total_refunded.toLocaleString('en-IN')} already refunded to customer
                </div>
              )}
            </div>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={openRefundModal} data-testid="record-refund-button">
              Record Refund
            </Button>
          </div>
        )}

        {/* Advanced Payment Summary Section */}
        {!loadingPaymentSummary && paymentSummary && (paymentSummary.has_quotation_advances || paymentSummary.invoice_advance_payments.length > 0) && (
          <div className="mb-8 border border-blue-200 rounded-lg bg-blue-50 p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              Payment History & Summary
            </h3>
            
            {/* Quotation Advance Payments */}
            {paymentSummary.has_quotation_advances && (
              <div className="mb-4">
                <h4 className="text-md font-medium text-blue-800 mb-2">
                  Advance from Quotation {paymentSummary.quotation_info?.quote_number}
                </h4>
                <div className="bg-white rounded p-3 mb-2">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Quotation Date:</span>
                      <div className="font-medium">
                        {paymentSummary.quotation_info?.quotation_date 
                          ? new Date(paymentSummary.quotation_info.quotation_date).toLocaleDateString('en-IN')
                          : 'N/A'
                        }
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Advance Amount:</span>
                      <div className="font-semibold text-green-600">
                        ₹{(paymentSummary.quotation_advance_total || 0).toLocaleString('en-IN')}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Payments:</span>
                      <div className="font-medium">
                        {paymentSummary.quotation_advance_payments.length} payment(s)
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Invoice Advance Payments */}
            {paymentSummary.invoice_advance_payments.length > 0 && (
              <div className="mb-4">
                <h4 className="text-md font-medium text-blue-800 mb-2">
                  Additional Advance (Direct Invoice)
                </h4>
                <div className="space-y-2">
                  {paymentSummary.invoice_advance_payments.map((payment, index) => (
                    <div key={payment.id || index} className="bg-white rounded p-3">
                      <div className="grid grid-cols-4 gap-3 text-sm">
                        <div>
                          <span className="text-gray-600">Amount:</span>
                          <div className="font-semibold text-green-600">
                            ₹{(payment.amount || 0).toLocaleString('en-IN')}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600">Method:</span>
                          <div className="font-medium capitalize">
                            {payment.payment_method?.replace('_', ' ') || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600">Date:</span>
                          <div className="font-medium">
                            {payment.payment_date 
                              ? new Date(payment.payment_date).toLocaleDateString('en-IN')
                              : 'N/A'
                            }
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600">Reference:</span>
                          <div className="font-medium text-xs">
                            {payment.reference_number || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Summary Totals */}
            <div className="border-t border-blue-300 pt-3">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-white rounded p-3">
                  <div className="text-sm text-gray-600">Total Invoice</div>
                  <div className="text-lg font-bold text-gray-900">
                    ₹{(paymentSummary.total_amount || 0).toLocaleString('en-IN')}
                  </div>
                </div>
                <div className="bg-white rounded p-3">
                  <div className="text-sm text-gray-600">Total Advance</div>
                  <div className="text-lg font-bold text-green-600">
                    ₹{(paymentSummary.total_advance_received || 0).toLocaleString('en-IN')}
                  </div>
                </div>
                <div className="bg-white rounded p-3">
                  <div className="text-sm text-gray-600">
                    {paymentSummary.is_overpaid ? 'Overpaid — Refund Due' : 'Balance Due'}
                  </div>
                  <div className={`text-lg font-bold ${paymentSummary.is_overpaid ? 'text-purple-600' : 'text-orange-600'}`} data-testid="invoice-balance-due-value">
                    ₹{(paymentSummary.is_overpaid ? paymentSummary.refund_due : (paymentSummary.balance_due || 0)).toLocaleString('en-IN')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Record Refund Dialog */}
        <Dialog open={showRefundModal} onOpenChange={setShowRefundModal}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Record Refund</DialogTitle>
              <DialogDescription>
                Customer overpaid by ₹{(paymentSummary?.refund_due || 0).toLocaleString('en-IN')}. Log the payout here once you've returned the money.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleRecordRefund} className="space-y-4">
              <div>
                <Label htmlFor="refund-amount">Refund Amount (₹) *</Label>
                <Input
                  id="refund-amount"
                  type="number"
                  min="0.01"
                  max={paymentSummary?.refund_due || 0}
                  step="0.01"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  data-testid="refund-amount-input"
                />
              </div>
              <div>
                <Label>Refund Via</Label>
                <Select value={refundMethod} onValueChange={setRefundMethod}>
                  <SelectTrigger data-testid="refund-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_account">Bank Account</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {refundMethod === 'bank_account' && (
                <div>
                  <Label>Select Bank Account *</Label>
                  <Select value={refundBankAccountId} onValueChange={setRefundBankAccountId}>
                    <SelectTrigger data-testid="refund-bank-account-select"><SelectValue placeholder="Choose bank account" /></SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map(a => (
                        <SelectItem key={a.id} value={a.id}>{a.bank_name} - **** {a.account_number?.slice(-4)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowRefundModal(false)}>Cancel</Button>
                <Button type="submit" disabled={submittingRefund} data-testid="refund-submit-button">
                  {submittingRefund ? 'Saving...' : 'Record Refund'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Payment History (all money-in events, chronological) */}
        {!loadingPaymentSummary && paymentSummary && (paymentSummary.payment_history || []).length > 0 && (
          <div className="mb-8 border border-gray-200 rounded-lg bg-white p-6" data-testid="invoice-payment-history">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-2 h-2 bg-green-600 rounded-full"></div>
              Payment History
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Type</th>
                    <th className="py-2 pr-4">Method</th>
                    <th className="py-2 pr-4">Reference</th>
                    <th className="py-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentSummary.payment_history.map((h, idx) => (
                    <tr key={idx} className="border-b last:border-0" data-testid={`payment-history-row-${idx}`}>
                      <td className="py-2 pr-4">
                        {h.date ? new Date(h.date).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td className="py-2 pr-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          h.kind === 'Discount' ? 'bg-amber-100 text-amber-700'
                          : h.kind === 'Refund' ? 'bg-purple-100 text-purple-700'
                          : h.kind === 'Payment' ? 'bg-green-100 text-green-700'
                          : 'bg-blue-100 text-blue-700'
                        }`}>
                          {h.kind}
                        </span>
                      </td>
                      <td className="py-2 pr-4 capitalize">
                        {h.payment_method ? String(h.payment_method).replace('_', ' ') : '—'}
                      </td>
                      <td className="py-2 pr-4 text-xs text-gray-500">{h.reference || '—'}</td>
                      <td className={`py-2 text-right font-semibold ${h.kind === 'Discount' || h.kind === 'Refund' ? 'text-red-600' : 'text-green-600'}`}>
                        {h.kind === 'Discount' || h.kind === 'Refund' ? '−' : ''}₹{(h.amount || 0).toLocaleString('en-IN')}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200">
                    <td className="py-2 pr-4 font-medium text-gray-700" colSpan={4}>Total Settled</td>
                    <td className="py-2 text-right font-bold text-gray-900" data-testid="payment-history-total-settled">
                      ₹{(
                        (paymentSummary.total_advance_received || 0)
                        + (paymentSummary.total_paid_received || 0)
                        + (paymentSummary.discount_amount || 0)
                        - (paymentSummary.total_refunded || 0)
                      ).toLocaleString('en-IN')}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-1 pr-4 font-medium text-gray-700" colSpan={4}>
                      {paymentSummary.is_overpaid ? 'Overpaid — Refund Due' : 'Balance Due'}
                    </td>
                    <td className={`py-1 text-right font-bold ${paymentSummary.is_overpaid ? 'text-purple-600' : 'text-orange-600'}`} data-testid="payment-history-balance-due">
                      ₹{(paymentSummary.is_overpaid ? paymentSummary.refund_due : (paymentSummary.balance_due || 0)).toLocaleString('en-IN')}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Loading state for payment summary */}
        {loadingPaymentSummary && (
          <div className="mb-8 border border-gray-200 rounded-lg bg-gray-50 p-6">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
              <span className="text-gray-600">Loading payment details...</span>
            </div>
          </div>
        )}

        {/* Notes */}
        {invoice.notes && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Notes:</h3>
            <p className="text-gray-700 text-sm bg-gray-50 p-4 rounded">{invoice.notes}</p>
          </div>
        )}

        {/* Status */}
        <div className="mb-8">
          <div className="inline-flex items-center">
            <span className="text-sm text-gray-600 mr-2">Status:</span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
              invoice.status === 'sent' ? 'bg-blue-100 text-blue-800' :
              invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {invoice.status ? invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1) : 'Draft'}
            </span>
          </div>
        </div>

        {/* Footer */}
        {!hideGarageInfo && (
          <div className="text-center text-sm text-gray-500 border-t border-gray-200 pt-4 footer">
            <p>Thank you for choosing our Garage for your automotive needs!</p>
          </div>
        )}

        {/* Spacer for sticky footer */}
        <div className="h-20"></div>
      </div>
      </div>

      {/* Sticky Footer with Actions */}
      <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 no-print">
        <div className="flex justify-center space-x-4">
          <Button
            variant="outline"
            onClick={() => setHideGarageInfo(v => !v)}
            className={hideGarageInfo ? 'border-orange-400 text-orange-600 hover:bg-orange-50' : ''}
            data-testid="toggle-garage-info-btn"
          >
            {hideGarageInfo ? 'Show Garage Info' : 'Hide Garage Info'}
          </Button>
          <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
            <Download className="h-4 w-4 mr-2" />
            Print Invoice
          </Button>
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

// Job Cards Section Component
// New Job Cards System - Enhanced Job Card Management

const InvoicePaymentForm = ({ invoice, onPaymentProcessed, onCancel }) => {
  // Prefer backend's authoritative balance_due; fall back to client-side calc
  // that accounts for both advances AND prior partial payments.
  const balanceDue = Math.max(
    0,
    invoice?.balance_due !== undefined && invoice?.balance_due !== null
      ? Number(invoice.balance_due)
      : (Number(invoice?.total_amount || 0)
         - Number(invoice?.total_advance_received || 0)
         - Number(invoice?.total_paid_received || 0)
         - Number(invoice?.discount_amount || 0))
  );
  
  const [paymentData, setPaymentData] = useState({
    payment_method: "cash",
    bank_account_id: "",
    credit_terms: "",
    amount_paid: balanceDue || 0,
    payment_notes: "",
    // Discount applied at payment time
    discount_enabled: false,
    discount_type: "amount",   // "amount" | "percentage"
    discount_value: 0,
    discount_reason: "",
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [cashBalance, setCashBalance] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Compute discount in flat ₹ regardless of whether user picked % or amount
  const computedDiscount = (() => {
    if (!paymentData.discount_enabled) return 0;
    const v = parseFloat(paymentData.discount_value) || 0;
    if (v <= 0) return 0;
    if (paymentData.discount_type === "percentage") {
      return Math.min(balanceDue, Math.round(balanceDue * (v / 100) * 100) / 100);
    }
    return Math.min(balanceDue, v);
  })();
  const effectiveAmountDue = Math.max(0, balanceDue - computedDiscount);

  // Update amount_paid when balanceDue or discount changes
  useEffect(() => {
    setPaymentData(prev => ({
      ...prev,
      amount_paid: effectiveAmountDue
    }));
  }, [balanceDue, computedDiscount, effectiveAmountDue]);

  // Fetch payment data on component mount
  useEffect(() => {
    const fetchPaymentData = async () => {
      try {
        // Fetch bank accounts using axios (consistent with working advance payment)
        const bankResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/bank-accounts`);
        console.log('Bank accounts data:', bankResponse.data); // Debug log
        setBankAccounts(bankResponse.data);

        // Fetch cash balance
        const cashResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/summary`);
        setCashBalance(cashResponse.data.cash_balance || 0);
      } catch (error) {
        console.error('Error fetching payment data:', error);
      }
    };

    fetchPaymentData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (paymentData.payment_method === 'bank_transfer' && !paymentData.bank_account_id) {
      toast.error("Please select a bank account for bank transfer payment");
      return;
    }

    if (paymentData.payment_method === 'credit' && !paymentData.credit_terms) {
      toast.error("Please select credit terms for credit facility payment");
      return;
    }

    if (paymentData.amount_paid <= 0) {
      toast.error("Please enter a valid payment amount greater than 0");
      return;
    }

    if (paymentData.amount_paid > balanceDue + 0.01) { // Use balanceDue instead of total_amount
      toast.error(`Payment amount (₹${paymentData.amount_paid.toLocaleString('en-IN')}) cannot exceed balance due (₹${balanceDue.toLocaleString('en-IN')})`);
      return;
    }

    if (paymentData.payment_method === 'bank_transfer' && !paymentData.bank_account_id) {
      toast.error("Please select a bank account for bank transfer payments");
      return;
    }

    if (paymentData.payment_method === 'credit' && !paymentData.credit_terms) {
      toast.error("Please select credit terms for credit payments");
      return;
    }

    setIsProcessing(true);

    try {
      // Use the new payment endpoint for proper accounting integration
      const paymentPayload = {
        amount: paymentData.amount_paid,
        payment_method: paymentData.payment_method,
        bank_account_id: paymentData.payment_method === 'bank_transfer' ? paymentData.bank_account_id : null,
        payment_date: new Date().toISOString(),
        reference_number: `PAY-${invoice.invoice_number}`,
        notes: paymentData.payment_notes || ''
      };

      // Apply discount if enabled
      if (paymentData.discount_enabled && computedDiscount > 0) {
        paymentPayload.discount_amount = computedDiscount;
        paymentPayload.discount_reason = paymentData.discount_reason.trim() || 'Discount given';
      }

      console.log('Payment payload being sent:', paymentPayload); // Debug log

      const authToken = localStorage.getItem('auth_token');
      if (!authToken) {
        toast.error('Your session has expired. Please log in again.');
        setIsProcessing(false);
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoice.id}/payment`,
        paymentPayload,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

    try {
      const paymentResult = response.data;
      
      // Get updated invoice data
      const invoiceResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoice.id}`);
      
      const updatedInvoice = invoiceResponse.data;
      toast.success(`Payment of ₹${paymentData.amount_paid.toLocaleString('en-IN')} processed successfully!`);
      onPaymentProcessed(updatedInvoice);
    } catch (error) {
      console.error('Payment API Error:', {
        error: error.response?.data || error.message,
        status: error.response?.status,
        payload: paymentPayload,
        invoice: invoice,
        authToken: localStorage.getItem('auth_token') ? 'Present' : 'Missing'
      });
      
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to process payment';
      toast.error(`Payment failed: ${errorMessage}`);
    }
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error('Error processing payment');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Invoice Summary */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-medium text-gray-900 mb-2">Payment Summary</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Invoice Number:</p>
            <p className="font-medium">{invoice.invoice_number}</p>
          </div>
          <div>
            <p className="text-gray-500">Customer:</p>
            <p className="font-medium">{invoice.customer_name}</p>
          </div>
          <div>
            <p className="text-gray-500">Original Amount:</p>
            <p className="font-medium text-gray-700">{formatCurrency(invoice.total_amount)}</p>
          </div>
          <div>
            <p className="text-gray-500">Current Status:</p>
            <Badge variant={invoice.status === 'paid' ? 'success' : 'warning'}>
              {invoice.status?.toUpperCase()}
            </Badge>
          </div>
          {(invoice.total_advance_received > 0) && (
            <div>
              <p className="text-gray-500">Advance Received:</p>
              <p className="font-medium text-blue-600">{formatCurrency(invoice.total_advance_received)}</p>
            </div>
          )}
          {Number(invoice.total_paid_received || 0) > 0 && (
            <div>
              <p className="text-gray-500">Total Paid So Far:</p>
              <p className="font-medium text-emerald-700" data-testid="invoice-total-paid-received">
                {formatCurrency(invoice.total_paid_received)}
              </p>
            </div>
          )}
          {Number(invoice.final_payment_amount || 0) > 0 && (
            <div className="col-span-2 border-t border-gray-200 pt-2 mt-1">
              <p className="text-gray-500 text-xs">Last Payment:</p>
              <p className="text-sm">
                <span className="font-semibold text-emerald-700">
                  {formatCurrency(invoice.final_payment_amount)}
                </span>
                <span className="text-gray-500"> via {(invoice.payment_method || '').replace('_', ' ') || '—'}</span>
                {invoice.final_payment_date && (
                  <span className="text-gray-500"> on {new Date(invoice.final_payment_date).toLocaleDateString('en-IN')}</span>
                )}
                {invoice.final_payment_reference && (
                  <span className="text-gray-400 text-xs"> · ref: {invoice.final_payment_reference}</span>
                )}
              </p>
            </div>
          )}
          {Number(invoice.discount_amount || 0) > 0 && (
            <div>
              <p className="text-gray-500">Discount Applied:</p>
              <p className="font-medium text-amber-700">− {formatCurrency(invoice.discount_amount)}</p>
            </div>
          )}
          <div>
            <p className="text-gray-500">{balanceDue < -0.01 ? 'Overpaid — Refund Due:' : 'Balance Due:'}</p>
            <p className={`font-semibold text-lg ${balanceDue < -0.01 ? 'text-purple-600' : 'text-orange-600'}`}>{formatCurrency(Math.abs(balanceDue))}</p>
          </div>
        </div>
        
        {balanceDue <= 0 && balanceDue >= -0.01 && (
          <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-center">
            <p className="text-green-700 font-medium">✅ Invoice Fully Paid</p>
          </div>
        )}
        {balanceDue < -0.01 && (
          <div className="mt-3 p-2 bg-purple-50 border border-purple-200 rounded text-center">
            <p className="text-purple-700 font-medium">Customer overpaid by {formatCurrency(Math.abs(balanceDue))} — use "Record Refund" on the invoice to settle it</p>
          </div>
        )}
      </div>

      {/* Discount Section (applied at payment time) */}
      {balanceDue > 0 && (
        <div className="border border-amber-200 bg-amber-50/40 p-4 rounded-lg" data-testid="payment-discount-section">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold text-amber-900">Apply Discount</h4>
              <p className="text-xs text-amber-700 mt-0.5">Optional — counter-negotiation, loyalty discount, write-off</p>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={paymentData.discount_enabled}
                onChange={(e) => setPaymentData(prev => ({
                  ...prev,
                  discount_enabled: e.target.checked,
                  discount_value: e.target.checked ? prev.discount_value : 0,
                  discount_reason: e.target.checked ? prev.discount_reason : "",
                }))}
                className="sr-only peer"
                data-testid="discount-toggle"
              />
              <div className="relative w-10 h-5 bg-gray-300 peer-checked:bg-amber-600 rounded-full transition-colors">
                <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${paymentData.discount_enabled ? 'translate-x-5' : ''}`}></div>
              </div>
            </label>
          </div>

          {paymentData.discount_enabled && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Discount Type</Label>
                <Select
                  value={paymentData.discount_type}
                  onValueChange={(v) => setPaymentData(prev => ({ ...prev, discount_type: v }))}
                >
                  <SelectTrigger className="mt-1 h-9" data-testid="discount-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="amount">Flat Amount (₹)</SelectItem>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">
                  Value {paymentData.discount_type === "percentage" ? "(%)" : "(₹)"}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max={paymentData.discount_type === "percentage" ? 100 : balanceDue}
                  className="mt-1 h-9"
                  placeholder={paymentData.discount_type === "percentage" ? "e.g. 10" : "e.g. 500"}
                  value={paymentData.discount_value}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, discount_value: e.target.value }))}
                  data-testid="discount-value-input"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Reason <span className="text-gray-400">(optional)</span></Label>
                <Input
                  type="text"
                  className="mt-1 h-9"
                  placeholder="e.g. Loyal customer, festival offer, rounded off"
                  value={paymentData.discount_reason}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, discount_reason: e.target.value }))}
                  data-testid="discount-reason-input"
                />
              </div>
              <div className="sm:col-span-2 bg-white rounded border border-amber-200 p-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-600">Balance Due:</span><span>{formatCurrency(balanceDue)}</span></div>
                <div className="flex justify-between text-amber-700"><span>Discount:</span><span>− {formatCurrency(computedDiscount)}</span></div>
                <div className="flex justify-between font-semibold border-t border-amber-200 pt-1 mt-1 text-green-700">
                  <span>Customer Pays:</span>
                  <span data-testid="customer-pays-amount">{formatCurrency(effectiveAmountDue)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment Method Selection */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Payment Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="payment_method">Payment Method *</Label>
            <Select 
              value={paymentData.payment_method} 
              onValueChange={(value) => setPaymentData(prev => ({
                ...prev, 
                payment_method: value,
                bank_account_id: value !== 'bank_transfer' ? '' : prev.bank_account_id
              }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select payment method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">💰 Cash Payment</SelectItem>
                <SelectItem value="bank_transfer">🏦 Bank Account Transfer</SelectItem>
                <SelectItem value="credit">📝 Credit Facility (Pay Later)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="amount_paid">Amount to Pay *</Label>
            <Input
              id="amount_paid"
              type="number"
              step="0.01"
              value={paymentData.amount_paid || 0}
              onChange={(e) => setPaymentData(prev => ({ 
                ...prev, 
                amount_paid: parseFloat(e.target.value) || 0 
              }))}
              className="no-uppercase"
              min="0.01"
              max={balanceDue}
              placeholder="Enter payment amount"
            />
          </div>
        </div>

        {/* Conditional Fields */}
        {paymentData.payment_method === 'bank_transfer' && (
          <div>
            <Label htmlFor="bank_account_id">Select Bank Account *</Label>
            <Select 
              value={paymentData.bank_account_id || ""} 
              onValueChange={(value) => setPaymentData(prev => ({...prev, bank_account_id: value}))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose bank account" />
              </SelectTrigger>
              <SelectContent>
                {bankAccounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.bank_name} - {account.account_number?.slice(-4)} ({formatCurrency(account.current_balance || 0)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {paymentData.payment_method === 'credit' && (
          <div>
            <Label htmlFor="credit_terms">Credit Terms *</Label>
            <Select 
              value={paymentData.credit_terms || ""} 
              onValueChange={(value) => setPaymentData(prev => ({...prev, credit_terms: value}))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select credit terms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="net_7">Net 7 Days</SelectItem>
                <SelectItem value="net_15">Net 15 Days</SelectItem>
                <SelectItem value="net_30">Net 30 Days</SelectItem>
                <SelectItem value="net_45">Net 45 Days</SelectItem>
                <SelectItem value="net_60">Net 60 Days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Payment Amount */}
        <div>
          <Label htmlFor="amount_paid">Payment Amount (₹) *</Label>
          <Input
            id="amount_paid"
            type="number"
            step="0.01"
            min="0.01"
            max={balanceDue}
            value={paymentData.amount_paid}
            onChange={(e) => setPaymentData(prev => ({...prev, amount_paid: parseFloat(e.target.value) || 0}))}
            placeholder="Enter payment amount"
            className="text-lg font-semibold"
          />
          <p className="text-xs text-gray-500 mt-1">
            Maximum amount: ₹{balanceDue.toLocaleString('en-IN')} (Balance Due)
          </p>
        </div>

        {/* Payment Notes */}
        <div>
          <Label htmlFor="payment_notes">Payment Notes (Optional)</Label>
          <Textarea
            id="payment_notes"
            value={paymentData.payment_notes || ""}
            onChange={(e) => setPaymentData(prev => ({...prev, payment_notes: e.target.value}))}
            placeholder="Enter any notes about this payment"
            rows={2}
          />
        </div>
      </div>

      {/* Payment Info — Invoice payment is incoming cash, so no insufficient-balance warning needed */}
      {paymentData.payment_method === 'cash' && paymentData.amount_paid > 0 && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <DollarSign className="h-4 w-4 text-green-600 mr-2" />
            <p className="text-sm text-green-800">
              💵 Receiving {formatCurrency(paymentData.amount_paid)} in cash from customer. Cash in hand will increase by this amount.
            </p>
          </div>
        </div>
      )}

      {paymentData.payment_method === 'credit' && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <Clock className="h-4 w-4 text-blue-600 mr-2" />
            <p className="text-sm text-blue-800">
              📋 Credit facility selected. Due date will be calculated based on selected terms.
            </p>
          </div>
        </div>
      )}

      {/* Submit Buttons */}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isProcessing}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={isProcessing || (paymentData.payment_method === 'bank_transfer' && !paymentData.bank_account_id) || paymentData.amount_paid <= 0 || paymentData.amount_paid > balanceDue}
          className="bg-green-600 hover:bg-green-700"
        >
          {isProcessing ? "Processing..." : `Receive Payment - ₹${paymentData.amount_paid.toLocaleString('en-IN')}`}
        </Button>
      </div>
    </form>
  );
};

// ============ Invoices Section Component ============

const InvoicesSection = ({ onUpdate }) => {
  const businessName = useGarageBranding();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [jobCards, setJobCards] = useState([]);
  const [parts, setParts] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCustomer, setFilterCustomer] = useState('all');
  const [customerFilterPopoverOpen, setCustomerFilterPopoverOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddInvoiceModalOpen, setIsAddInvoiceModalOpen] = useState(false);
  const [isEditInvoiceModalOpen, setIsEditInvoiceModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [isViewInvoiceModalOpen, setIsViewInvoiceModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);

  // Create-from-quotation states
  const [quotations, setQuotations] = useState([]);
  const [showFromQuotationDialog, setShowFromQuotationDialog] = useState(false);
  const [convertingQuotationId, setConvertingQuotationId] = useState('');
  
  // Advance Payment States
  const [showAdvancePaymentDialog, setShowAdvancePaymentDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [advancePaymentData, setAdvancePaymentData] = useState({
    amount: '',
    payment_method: 'cash',
    bank_account_id: '',
    reference_number: '',
    notes: '',
    percentage: '',
    is_percentage: false
  });
  const [bankAccounts, setBankAccounts] = useState([]);

  // Sample invoices data with parts and services integration
  const sampleInvoices = [
    {
      id: "inv-001",
      invoice_number: "INV-2025-001",
      customer_id: "cust-001", 
      customer_name: "Anita Sharma",
      customer_phone: "+91 89123 67890",
      customer_address: "Sector 14, Gurgaon, Haryana 122001",
      invoice_date: "2025-01-08",
      due_date: "2025-01-23",
      job_card_id: "JC-2025-003",
      vehicle_info: "2022 Maruti Swift (HR-26-AB-1234)",
      vehicle_details: {
        vin: "MA3EJEB1S00A12345",
        make: "Maruti Suzuki",
        model: "Swift",
        year: 2022,
        mileage: 25000,
        license_plate: "HR-26-AB-1234",
        engine_no: "G12B123456",
        fuel_type: "Petrol",
        transmission: "Manual"
      },
      items: [
        {
          id: "item-001",
          type: "service",
          service_id: "service-002",
          description: "Complete Brake Service - Brake pad replacement, rotor resurfacing, brake fluid flush",
          quantity: 1,
          unit_price: 6300.00,
          total: 6300.00
        },
        {
          id: "item-002", 
          type: "part",
          part_id: "part-001",
          description: "Brake Pads (Set of 4) - Premium quality ceramic brake pads",
          quantity: 1,
          unit_price: 2500.00,
          total: 2500.00
        },
        {
          id: "item-003",
          type: "part",
          part_id: "part-002",
          description: "Brake Fluid (1L) - DOT 3 brake fluid",
          quantity: 2,
          unit_price: 450.00,
          total: 900.00
        }
      ],
      subtotal: 9700.00,
      total_amount: 9700.00,
      status: "paid",
      payment_date: "2025-01-10",
      payment_method: "UPI",
      notes: "Brake service completed successfully. Vehicle tested and ready for pickup.",
      created_at: "2025-01-08T10:30:00Z"
    },
    {
      id: "inv-002",
      invoice_number: "INV-2025-002",
      customer_id: "cust-002",
      customer_name: "Rajesh Kumar",
      customer_phone: "+91 906173 9191",
      customer_address: "Plot 45, Industrial Area, Faridabad, Haryana 121001",
      invoice_date: "2025-01-09",
      due_date: "2025-01-24",
      job_card_id: "JC-2025-001",
      vehicle_info: "2023 Toyota Corolla (ABC-123)",
      vehicle_details: {
        vin: "1NXBR32E25Z123456",
        make: "Toyota",
        model: "Corolla",
        year: 2023,
        mileage: 15000,
        license_plate: "ABC-123",
        engine_no: "2ZR-FE789012",
        fuel_type: "Petrol",
        transmission: "CVT Automatic"
      },
      items: [
        {
          id: "item-004",
          type: "service",
          service_id: "service-001",
          description: "Basic Oil Change - Engine oil and filter replacement service",
          quantity: 1,
          unit_price: 3000.00,
          total: 3000.00
        },
        {
          id: "item-005",
          type: "part",
          part_id: "part-003",
          description: "Engine Oil (5L) - Synthetic 5W-30 motor oil",
          quantity: 1,
          unit_price: 2800.00,
          total: 2800.00
        },
        {
          id: "item-006",
          type: "service",
          service_id: "service-006",
          description: "Battery & Electrical Check - Battery testing, alternator check, starter inspection",
          quantity: 1,
          unit_price: 825.00,
          total: 825.00
        }
      ],
      subtotal: 6625.00,
      total_amount: 6625.00,
      status: "sent",
      payment_date: null,
      payment_method: null,
      notes: "Oil change and electrical system check completed. Invoice sent via email.",
      created_at: "2025-01-09T14:15:00Z"
    },
    {
      id: "inv-003",
      invoice_number: "INV-2025-003",
      customer_id: "cust-003",
      customer_name: "Priya Singh",
      customer_phone: "+91 87654 32109",
      customer_address: "B-204, Green Valley, New Delhi 110001",
      invoice_date: "2025-01-10",
      due_date: "2025-01-25",
      job_card_id: null,
      vehicle_info: "2021 Honda City (DL-8C-AB-1234)",
      vehicle_details: {
        vin: "MAGA8BF1SMC123456",
        make: "Honda",
        model: "City",
        year: 2021,
        mileage: 35000,
        license_plate: "DL-8C-AB-1234",
        engine_no: "L15A7345678",
        fuel_type: "Petrol",
        transmission: "Manual"
      },
      items: [
        {
          id: "item-007",
          type: "service",
          service_id: "service-003",
          description: "AC System Diagnostic - Complete air conditioning system diagnosis and gas refill",
          quantity: 1,
          unit_price: 4000.00,
          total: 4000.00
        },
        {
          id: "item-008",
          type: "part",
          part_id: "part-004",
          description: "AC Gas (R134a) - 500g refrigerant",
          quantity: 1,
          unit_price: 1200.00,
          total: 1200.00
        }
      ],
      subtotal: 5200.00,
      total_amount: 5200.00,
      status: "draft",
      payment_date: null,
      payment_method: null,
      notes: "AC gas refill and system diagnosis. Waiting for customer approval.",
      created_at: "2025-01-10T11:45:00Z"
    }
  ];

  // Sample parts and services data (would typically come from existing sections)
  const sampleParts = [
    { id: "part-001", name: "Brake Pads (Set)", description: "Premium ceramic brake pads", cost_price: 2000.00, selling_price: 2500.00, stock: 25 },
    { id: "part-002", name: "Brake Fluid (1L)", description: "DOT 3 brake fluid", cost_price: 350.00, selling_price: 450.00, stock: 50 },
    { id: "part-003", name: "Engine Oil (5L)", description: "Synthetic 5W-30 motor oil", cost_price: 2200.00, selling_price: 2800.00, stock: 30 },
    { id: "part-004", name: "AC Gas (R134a)", description: "500g refrigerant", cost_price: 900.00, selling_price: 1200.00, stock: 15 },
    { id: "part-005", name: "Air Filter", description: "High efficiency air filter", cost_price: 600.00, selling_price: 850.00, stock: 40 }
  ];

  const sampleServices = [
    { id: "service-001", name: "Basic Oil Change", description: "Engine oil and filter replacement", price: 3000.00, duration: 30 },
    { id: "service-002", name: "Complete Brake Service", description: "Brake pad replacement, rotor resurfacing, brake fluid flush", price: 6300.00, duration: 120 },
    { id: "service-003", name: "AC System Diagnostic", description: "Complete air conditioning system diagnosis and gas refill", price: 4000.00, duration: 90 },
    { id: "service-004", name: "Engine Tune-Up", description: "Spark plugs, air filter, fuel system cleaning", price: 5450.00, duration: 150 },
    { id: "service-005", name: "Transmission Service", description: "Transmission fluid change and filter replacement", price: 4100.00, duration: 75 },
    { id: "service-006", name: "Battery & Electrical Check", description: "Battery testing, alternator check, starter inspection", price: 825.00, duration: 45 }
  ];
  const sampleJobCards = [
    {
      id: "JC-2025-001",
      job_number: "JC-2025-001",
      vehicle_id: "vehicle-001",
      customer_name: "Rajesh Kumar",
      vehicle_make: "Toyota",
      vehicle_model: "Corolla",
      vehicle_reg: "ABC-123",
      year: 2023,
      status: "in-progress",
      assigned_technician: "Amit Singh",
      description: "Engine oil change and brake inspection",
      total_cost: 3500.00,
      created_at: "2025-01-09T10:00:00Z"
    },
    {
      id: "JC-2025-002",
      job_number: "JC-2025-002",
      vehicle_id: "vehicle-002",
      customer_name: "Priya Sharma",
      vehicle_make: "Honda",
      vehicle_model: "City",
      vehicle_reg: "XYZ-789",
      year: 2021,
      status: "completed",
      assigned_technician: "Vikram Patel",
      description: "AC system repair and gas refill",
      total_cost: 6200.00,
      created_at: "2025-01-08T14:30:00Z"
    },
    {
      id: "JC-2025-003",
      job_number: "JC-2025-003",
      vehicle_id: "vehicle-003",
      customer_name: "Anita Sharma",
      vehicle_make: "Maruti Suzuki",
      vehicle_model: "Swift",
      vehicle_reg: "HR-26-AB-1234",
      year: 2022,
      status: "completed",
      assigned_technician: "Ravi Kumar",
      description: "Complete brake service with pad replacement",
      total_cost: 8500.00,
      created_at: "2025-01-07T09:15:00Z"
    }
  ];

  // Fetch real data from API
  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/enhanced`);
      setVehicles(response.data);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      setVehicles([]);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/invoices`);
      setInvoices(response.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      // Fallback to sample data if API fails
      setInvoices(sampleInvoices);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/bank-accounts`);
      setBankAccounts(response.data);
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
      setBankAccounts([]);
    }
  };

  const fetchJobCards = async () => {
    try {
      const response = await axios.get(`${API}/jobcards`);
      setJobCards(response.data);
    } catch (error) {
      console.error("Error fetching job cards:", error);
      setJobCards([]);
    }
  };

  const fetchParts = async () => {
    try {
      const response = await axios.get(`${API}/parts`);
      setParts(response.data);
    } catch (error) {
      console.error("Error fetching parts:", error);
      // Fallback to sample data if API fails
      setParts(sampleParts);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
      // Fallback to sample data if API fails
      setServices(sampleServices);
    }
  };

  const fetchQuotations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/quotations`);
      setQuotations(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching quotations:", error);
      setQuotations([]);
    }
  };

  // Convert a quotation to an invoice from the Invoices page
  const handleConvertQuotationToInvoice = async (quotation, allowMultiple = false) => {
    setConvertingQuotationId(quotation.id);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/invoices/from-quotation/${quotation.id}`,
        { allow_multiple: allowMultiple }
      );
      const invoice = response.data;
      toast.success(`Invoice ${invoice.invoice_number} created from ${quotation.quote_number}`, {
        description: (invoice.total_advance_received > 0)
          ? `Advance applied: ₹${Number(invoice.total_advance_received).toLocaleString('en-IN')} · Balance due: ₹${Number(invoice.balance_due).toLocaleString('en-IN')}`
          : `Balance due: ₹${Number(invoice.balance_due).toLocaleString('en-IN')}`
      });
      setShowFromQuotationDialog(false);
      await Promise.all([fetchInvoices(), fetchQuotations()]);
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 409 && !allowMultiple) {
        const proceed = window.confirm(
          `${typeof detail === 'string' ? detail : 'This quotation is already converted.'}\n\nDo you want to create ANOTHER invoice from this quotation anyway?`
        );
        if (proceed) return handleConvertQuotationToInvoice(quotation, true);
        return;
      }
      console.error("Error converting quotation:", error);
      toast.error(typeof detail === 'string' ? detail : 'Failed to convert quotation to invoice');
    } finally {
      setConvertingQuotationId('');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      // Fetch real data from API
      await Promise.all([
        fetchCustomers(), 
        fetchVehicles(), 
        fetchInvoices(), 
        fetchJobCards(),
        fetchParts(),
        fetchServices(),
        fetchBankAccounts(),
        fetchQuotations()
      ]);
      
      setLoading(false);
    };
    
    loadData();
  }, []);

  // Filter invoices
  const filteredInvoices = invoices.filter(invoice => {
    // Skip undefined or null invoices
    if (!invoice) return false;
    
    const matchesStatus = filterStatus === 'all' || invoice.status === filterStatus;
    const matchesCustomer = filterCustomer === 'all' || invoice.customer_id === filterCustomer;
    const matchesSearch = searchTerm === '' || 
      (invoice.invoice_number && invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (invoice.customer_name && invoice.customer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (invoice.vehicle_info && invoice.vehicle_info.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesStatus && matchesCustomer && matchesSearch;
  }).sort((a, b) => {
    // Newest first: prefer created_at, fall back to invoice_date, then invoice_number
    const aKey = a.created_at || a.invoice_date || a.invoice_number || '';
    const bKey = b.created_at || b.invoice_date || b.invoice_number || '';
    return String(bKey).localeCompare(String(aKey));
  });

  // Statistics
  const validInvoices = invoices.filter(i => i != null);
  // Per-invoice "amount actually collected" — works for any status:
  //   - paid:      full total_amount (also covers legacy data missing total_paid_received)
  //   - partial:   prefer the cumulative total_paid_received; if absent, derive from total - balance_due
  //   - others:    sum of advance + paid_received (handles draft/sent with advance payments)
  const computeReceived = (inv) => {
    const total = Number(inv.total_amount || 0);
    if (inv.status === 'paid') {
      return Number(inv.total_paid_received || total);
    }
    const cumulative = Number(inv.total_paid_received || 0);
    if (cumulative > 0) {
      return Math.min(cumulative, total);
    }
    // Fallback for legacy records: total - balance_due
    if (inv.balance_due !== undefined && inv.balance_due !== null) {
      return Math.max(0, total - Number(inv.balance_due));
    }
    // Last resort: advance only
    return Number(inv.total_advance_received || 0);
  };

  const invoiceStats = {
    total: validInvoices.length,
    draft: validInvoices.filter(i => i.status === 'draft').length,
    sent: validInvoices.filter(i => i.status === 'sent').length,
    paid: validInvoices.filter(i => i.status === 'paid').length,
    partial: validInvoices.filter(i => i.status === 'partial').length,
    overdue: validInvoices.filter(i => i.status === 'overdue').length,
    totalValue: validInvoices.reduce((sum, i) => sum + (i.total_amount || 0), 0),
    // Paid Value now includes partial payments — sums actual money collected
    // across every invoice (paid + partial + any advances on draft/sent).
    paidValue: validInvoices.reduce((sum, i) => sum + computeReceived(i), 0),
  };

  const invoiceStatusConfig = {
    "draft": { color: "bg-gray-100 text-gray-800", icon: FileText, label: "Draft" },
    "sent": { color: "bg-blue-100 text-blue-800", icon: Mail, label: "Sent" },
    "partial": { color: "bg-amber-100 text-amber-800", icon: Clock, label: "Partial" },
    "paid": { color: "bg-green-100 text-green-800", icon: CheckCircle, label: "Paid" },
    "overdue": { color: "bg-red-100 text-red-800", icon: AlertCircle, label: "Overdue" },
    "cancelled": { color: "bg-orange-100 text-orange-800", icon: X, label: "Cancelled" }
  };

  const handleAddInvoice = () => {
    setIsAddInvoiceModalOpen(true);
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setIsEditInvoiceModalOpen(true);
  };

  const handleViewInvoice = (invoice) => {
    setViewingInvoice(invoice);
    setIsViewInvoiceModalOpen(true);
  };

  const handleInvoiceAdded = (newInvoice) => {
    // Add the new invoice from backend response to the list
    setInvoices([...invoices, newInvoice]);
    setIsAddInvoiceModalOpen(false);
    
    // Optionally refresh the entire list to ensure consistency
    fetchInvoices();
  };

  const handleInvoiceUpdated = (updatedInvoice) => {
    setInvoices(invoices.map(i => 
      i.id === updatedInvoice.id ? updatedInvoice : i
    ));
    setIsEditInvoiceModalOpen(false);
    setEditingInvoice(null);
    if (onUpdate) onUpdate();
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;
    
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoiceId}`);
      setInvoices(invoices.filter(i => i.id !== invoiceId));
      toast.success("Invoice deleted successfully!");
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error deleting invoice:", error);
      toast.error("Failed to delete invoice");
    }
  };

  const updateInvoiceStatus = async (invoiceId, newStatus) => {
    try {
      const updateData = {
        status: newStatus,
        payment_date: newStatus === 'paid' ? new Date().toISOString() : null,
        payment_method: newStatus === 'paid' ? 'Cash' : null
      };

      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoiceId}`, updateData);
      
      setInvoices(invoices.map(i => 
        i.id === invoiceId 
          ? { 
              ...i, 
              status: newStatus,
              payment_date: newStatus === 'paid' ? new Date().toISOString().split('T')[0] : null,
              payment_method: newStatus === 'paid' ? 'Cash' : null
            }
          : i
      ));
      
      toast.success(`Invoice ${newStatus === 'paid' ? 'marked as paid' : 'status updated'} successfully!`);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error updating invoice status:", error);
      toast.error("Failed to update invoice status");
    }
  };

  // Advance Payment Functions for Invoices
  const handleAdvancePayment = async () => {
    try {
      if (!selectedInvoice) {
        toast.error('No invoice selected');
        return;
      }

      let finalAmount;
      if (advancePaymentData.is_percentage) {
        if (!advancePaymentData.percentage || parseFloat(advancePaymentData.percentage) <= 0 || parseFloat(advancePaymentData.percentage) > 100) {
          toast.error('Please enter valid percentage (1-100%)');
          return;
        }
        finalAmount = (selectedInvoice.total_amount * parseFloat(advancePaymentData.percentage)) / 100;
      } else {
        if (!advancePaymentData.amount || parseFloat(advancePaymentData.amount) <= 0) {
          toast.error('Please enter valid amount');
          return;
        }
        finalAmount = parseFloat(advancePaymentData.amount);
      }

      // Check if advance doesn't exceed remaining balance
      const currentBalance = selectedInvoice.total_amount - (selectedInvoice.total_advance_received || 0);
      if (finalAmount > currentBalance) {
        toast.error(`Advance amount cannot exceed remaining balance of ₹${currentBalance.toLocaleString('en-IN')}`);
        return;
      }

      // Validate bank account selection for bank transfers
      if (advancePaymentData.payment_method === 'bank_transfer' && !advancePaymentData.bank_account_id) {
        toast.error('Please select a bank account for bank transfer');
        return;
      }

      // Validate bank account selection for bank transfer
      if (advancePaymentData.payment_method === 'bank_transfer' && !advancePaymentData.bank_account_id) {
        toast.error('Please select a bank account for bank transfer');
        return;
      }

      const paymentData = {
        amount: finalAmount,
        payment_method: advancePaymentData.payment_method,
        bank_account_id: advancePaymentData.bank_account_id || null,
        reference_number: advancePaymentData.reference_number || null,
        notes: advancePaymentData.notes || null
      };

      // Call backend API to record advance payment for invoice
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${selectedInvoice.id}/advance-payment`,
        paymentData
      );

      // Update local invoice data
      const updatedInvoice = {
        ...selectedInvoice,
        advance_payments: [...(selectedInvoice.advance_payments || []), response.data.advance_payment],
        total_advance_received: response.data.total_advance_received,
        balance_due: response.data.balance_due,
        // Mark as paid if fully paid with advance
        is_paid: response.data.balance_due <= 0
      };

      setSelectedInvoice(updatedInvoice);
      
      // Update invoices list
      setInvoices(prev => prev.map(inv => inv.id === selectedInvoice.id ? updatedInvoice : inv));

      toast.success('Advance payment recorded successfully');
      setShowAdvancePaymentDialog(false);
      setAdvancePaymentData({
        amount: '',
        payment_method: 'cash',
        bank_account_id: '',
        reference_number: '',
        notes: '',
        percentage: '',
        is_percentage: false
      });

      if (onUpdate) onUpdate(); // Update parent component if needed
    } catch (error) {
      console.error('Error recording advance payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to record advance payment');
    }
  };

  const generateInvoiceAdvanceReceipt = (payment, invoice) => {
    const receiptContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Advance Payment Receipt - ${invoice.invoice_number}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #2563eb; padding-bottom: 15px; }
          .company-name { font-size: 24px; font-weight: bold; color: #2563eb; }
          .receipt-title { font-size: 18px; color: #1f2937; margin: 10px 0; }
          .info-section { margin: 15px 0; padding: 10px; background-color: #f8fafc; border-left: 4px solid #2563eb; }
          .info-row { display: flex; justify-content: space-between; margin: 5px 0; }
          .info-label { font-weight: bold; }
          .amount-section { background-color: #f0f9ff; padding: 15px; border: 2px solid #2563eb; border-radius: 8px; text-align: center; margin: 20px 0; }
          .amount { font-size: 24px; font-weight: bold; color: #2563eb; }
          .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">${businessName.toUpperCase()}</div>
          <div class="receipt-title">ADVANCE PAYMENT RECEIPT</div>
        </div>

        <div class="info-section">
          <div class="info-row">
            <span class="info-label">Receipt Date:</span>
            <span>${new Date().toLocaleDateString('en-IN')}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Payment Date:</span>
            <span>${new Date(payment.payment_date).toLocaleDateString('en-IN')}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Invoice Number:</span>
            <span>${invoice.invoice_number}</span>
          </div>
        </div>

        <div class="info-section">
          <div class="info-row">
            <span class="info-label">Customer:</span>
            <span>${invoice.customer_name}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Vehicle:</span>
            <span>${invoice.vehicle_info}</span>
          </div>
        </div>

        <div class="amount-section">
          <div>ADVANCE PAYMENT RECEIVED</div>
          <div class="amount">₹${payment.amount.toLocaleString('en-IN')}</div>
          <div style="margin-top: 10px;">
            <strong>Payment Method:</strong> ${payment.payment_method.replace('_', ' ').toUpperCase()}
            ${payment.reference_number ? `<br><strong>Reference:</strong> ${payment.reference_number}` : ''}
          </div>
        </div>

        <div class="info-section">
          <div class="info-row">
            <span class="info-label">Invoice Amount:</span>
            <span>₹${invoice.total_amount.toLocaleString('en-IN')}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Total Advance Received:</span>
            <span>₹${invoice.total_advance_received.toLocaleString('en-IN')}</span>
          </div>
          <div class="info-row" style="border-top: 1px solid #ccc; padding-top: 5px; margin-top: 5px;">
            <span class="info-label"><strong>Remaining Balance:</strong></span>
            <span><strong>₹${(invoice.total_amount - invoice.total_advance_received).toLocaleString('en-IN')}</strong></span>
          </div>
        </div>

        ${payment.notes ? `
        <div class="info-section">
          <div class="info-label">Notes:</div>
          <div style="margin-top: 5px;">${payment.notes}</div>
        </div>
        ` : ''}

        <div class="footer">
          <p><strong>Thank you for your advance payment!</strong></p>
          <p>This receipt is valid proof of advance payment received by the Garage.</p>
          <p>Balance amount will be collected upon completion of services.</p>
        </div>
      </body>
      </html>
    `;
    
    // Open print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(receiptContent);
    printWindow.document.close();
    printWindow.onload = function() {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }, 250);
    };
  };

  // Invoice Statement: Export CSV / Print PDF (respects current search/status/customer filters)
  const buildInvoiceStatementRows = () => filteredInvoices.map(inv => ({
    invoice_number: inv.invoice_number || '\u2014',
    date: (inv.invoice_date || (inv.created_at || '').slice(0, 10) || '\u2014').slice(0, 10),
    customer_name: inv.customer_name || '\u2014',
    vehicle: inv.vehicle_info || inv.vehicle_registration || '\u2014',
    status: inv.status || '\u2014',
    total_amount: Number(inv.total_amount || 0),
    paid_amount: Number(inv.total_paid_received || (inv.total_amount || 0) - (inv.balance_due || 0) || 0),
    balance_due: Number(inv.balance_due ?? Math.max(0, (inv.total_amount || 0) - (inv.total_paid_received || 0))),
  }));

  const formatMoney = (amt) => `\u20b9${Number(amt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleExportInvoiceStatementCSV = () => {
    const rows = buildInvoiceStatementRows();
    exportRowsToCSV(
      `invoice-statement-${toLocalISODate(new Date())}`,
      ['Invoice No', 'Date', 'Customer', 'Vehicle', 'Status', 'Total Amount', 'Paid Amount', 'Balance Due'],
      rows.map(r => [r.invoice_number, r.date, r.customer_name, r.vehicle, r.status, r.total_amount, r.paid_amount, r.balance_due])
    );
  };

  const handlePrintInvoiceStatement = () => {
    const rows = buildInvoiceStatementRows();
    const totals = rows.reduce((acc, r) => ({
      total: acc.total + r.total_amount, paid: acc.paid + r.paid_amount, balance: acc.balance + r.balance_due
    }), { total: 0, paid: 0, balance: 0 });
    const tableRows = rows.map(r => `<tr>
      <td>${r.invoice_number}</td><td>${r.date}</td><td>${r.customer_name}</td><td>${r.vehicle}</td>
      <td style="text-transform:capitalize">${r.status}</td>
      <td style="text-align:right">${formatMoney(r.total_amount)}</td>
      <td style="text-align:right">${formatMoney(r.paid_amount)}</td>
      <td style="text-align:right">${formatMoney(r.balance_due)}</td>
    </tr>`).join('');
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>Invoice Statement</title>
      <style>body{font-family:Arial,sans-serif;padding:24px;color:#111} h1{font-size:20px;margin-bottom:4px} p{color:#555;font-size:12px;margin-top:0} table{width:100%;border-collapse:collapse;margin-top:16px} th,td{border:1px solid #ddd;padding:6px 8px;text-align:left;font-size:12px} th{background:#f3f4f6} tfoot td{font-weight:bold;background:#f9fafb}</style>
      </head><body>
      <h1>Invoice Statement</h1>
      <p>Generated on ${new Date().toLocaleString('en-IN')} \u2014 ${rows.length} invoice(s)</p>
      <table><thead><tr><th>Invoice No</th><th>Date</th><th>Customer</th><th>Vehicle</th><th>Status</th><th>Total Amount</th><th>Paid Amount</th><th>Balance Due</th></tr></thead>
      <tbody>${tableRows}</tbody>
      <tfoot><tr><td colspan="5">TOTAL</td><td style="text-align:right">${formatMoney(totals.total)}</td><td style="text-align:right">${formatMoney(totals.paid)}</td><td style="text-align:right">${formatMoney(totals.balance)}</td></tr></tfoot>
      </table>
      </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Invoices Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Invoice Management</h2>
          <p className="text-gray-600">Create invoices with integrated parts and services, GST calculations</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleExportInvoiceStatementCSV}
            variant="outline"
            data-testid="invoice-statement-export-csv-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button
            onClick={handlePrintInvoiceStatement}
            variant="outline"
            data-testid="invoice-statement-print-btn"
          >
            <FileText className="h-4 w-4 mr-2" />
            Print Statement
          </Button>
          <Button
            onClick={() => { fetchQuotations(); setShowFromQuotationDialog(true); }}
            variant="outline"
            className="border-indigo-600 text-indigo-600 hover:bg-indigo-50"
            data-testid="create-from-quotation-btn"
          >
            <FileText className="h-4 w-4 mr-2" />
            Create from Quotation
          </Button>
          <Button 
            onClick={handleAddInvoice}
            className="bg-green-600 hover:bg-green-700"
            data-testid="add-invoice-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Invoice
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{invoiceStats.total}</div>
                <div className="text-sm opacity-90">Total Invoices</div>
              </div>
              <FileText className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-gray-500 to-gray-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{invoiceStats.draft}</div>
                <div className="text-sm opacity-90">Draft</div>
              </div>
              <FileText className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-blue-400 to-blue-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{invoiceStats.sent}</div>
                <div className="text-sm opacity-90">Sent</div>
              </div>
              <Mail className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{invoiceStats.paid}</div>
                <div className="text-sm opacity-90">Paid</div>
              </div>
              <CheckCircle className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{invoiceStats.overdue}</div>
                <div className="text-sm opacity-90">Overdue</div>
              </div>
              <AlertCircle className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">₹{(invoiceStats.totalValue || 0).toLocaleString('en-IN')}</div>
                <div className="text-sm opacity-90">Total Value</div>
              </div>
              <DollarSign className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">₹{(invoiceStats.paidValue || 0).toLocaleString('en-IN')}</div>
                <div className="text-sm opacity-90">Paid Value</div>
              </div>
              <CheckCircle className="h-6 w-6 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <Label>Search Invoices</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by invoice number, customer, vehicle..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 no-uppercase"
                />
              </div>
            </div>

            <div>
              <Label>Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Customer</Label>
              <Popover open={customerFilterPopoverOpen} onOpenChange={setCustomerFilterPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={customerFilterPopoverOpen}
                    className="w-full justify-between font-normal"
                    data-testid="invoice-customer-filter"
                  >
                    <span className="truncate text-left">
                      {filterCustomer === 'all'
                        ? 'All Customers'
                        : (customers.find(c => c.id === filterCustomer)?.name || 'All Customers')}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search customer..." data-testid="invoice-customer-filter-search-input" />
                    <CommandList className="max-h-60">
                      <CommandEmpty>No customer found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="all-customers"
                          onSelect={() => { setFilterCustomer('all'); setCustomerFilterPopoverOpen(false); }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${filterCustomer === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                          All Customers
                        </CommandItem>
                        {customers
                          .filter(customer => customer && customer.id && customer.name && customer.name.trim() !== '')
                          .map(customer => (
                            <CommandItem
                              key={customer.id}
                              value={customer.name}
                              onSelect={() => { setFilterCustomer(customer.id); setCustomerFilterPopoverOpen(false); }}
                              data-testid={`invoice-customer-filter-option-${customer.id}`}
                            >
                              <Check className={`mr-2 h-4 w-4 ${filterCustomer === customer.id ? 'opacity-100' : 'opacity-0'}`} />
                              {customer.name}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoices List */}
      <div className="space-y-4">
        {filteredInvoices.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Invoices Found</h3>
              <p className="text-gray-600">
                {invoices.length === 0 
                  ? "No invoices created yet. Create your first invoice to get started."
                  : "No invoices match your current filters. Try adjusting your search criteria."
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredInvoices.map((invoice) => {
            const effectiveStatus = invoice.status || 'draft';
            const statusInfo = invoiceStatusConfig[effectiveStatus] || { color: "bg-gray-100 text-gray-800", label: "Unknown", icon: Package };
            
            return (
              <Card key={invoice.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <FileText className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{invoice.invoice_number}</h3>
                          <p className="text-sm text-gray-600">{invoice.customer_name} • {invoice.vehicle_info}</p>
                        </div>
                        <Badge className={`${statusInfo.color} text-xs`}>
                          <statusInfo.icon className="h-3 w-3 mr-1" />
                          {statusInfo.label}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Invoice Date</p>
                          <p className="text-sm text-gray-600">
                            {new Date(invoice.invoice_date).toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Due Date</p>
                          <p className="text-sm text-gray-600">
                            {new Date(invoice.due_date).toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Items</p>
                          <p className="text-sm text-gray-600">{invoice.items.length} items</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Total Amount</p>
                          <p className="text-lg font-semibold text-green-600">
                            ₹{(invoice.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        
                        {/* Advance Payment Information */}
                        {(invoice.total_advance_received > 0) && (
                          <div>
                            <p className="text-sm font-medium text-gray-700">Advance Received</p>
                            <p className="text-sm font-semibold text-blue-600">
                              ₹{(invoice.total_advance_received || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                        )}
                        
                        {(invoice.total_advance_received > 0) && (
                          <div>
                            <p className="text-sm font-medium text-gray-700">
                              {(invoice.balance_due || 0) < -0.01 ? 'Overpaid — Refund Due' : 'Balance Due'}
                            </p>
                            <p className={`text-lg font-semibold ${(invoice.balance_due || 0) < -0.01 ? 'text-purple-600' : 'text-orange-600'}`}>
                              ₹{Math.abs(invoice.balance_due != null ? invoice.balance_due : ((invoice.total_amount || 0) - (invoice.total_advance_received || 0))).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Detailed Vehicle Information */}
                      {invoice.vehicle_details && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Vehicle Details:</p>
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="font-medium text-gray-700">VIN:</span>
                                <span className="text-gray-600 ml-2">{invoice.vehicle_details.vin}</span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">License Plate:</span>
                                <span className="text-gray-600 ml-2">{invoice.vehicle_details.license_plate}</span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">Model:</span>
                                <span className="text-gray-600 ml-2">{invoice.vehicle_details.year} {invoice.vehicle_details.make} {invoice.vehicle_details.model}</span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">Mileage:</span>
                                <span className="text-gray-600 ml-2">{(invoice.vehicle_details?.mileage || 0).toLocaleString()} km</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Invoice Items Preview */}
                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Invoice Items:</p>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          {invoice.items.slice(0, 2).map((item, index) => (
                            <div key={item.id || index} className="flex justify-between items-center text-xs text-gray-600">
                              <span>
                                <Badge variant="outline" className="mr-2 text-xs">
                                  {(item.item_type || item.type) === 'service' ? 'Service' : 'Part'}
                                </Badge>
                                {item.description && item.description.length > 50 ? item.description.substring(0, 50) + '...' : item.description || 'No description'}
                              </span>
                              <span className="font-medium">₹{(item.amount ?? item.total ?? 0).toFixed(2)}</span>
                            </div>
                          ))}
                          {invoice.items.length > 2 && (
                            <p className="text-xs text-gray-500 mt-2">+{invoice.items.length - 2} more items</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>Created: {new Date(invoice.created_at).toLocaleDateString()}</span>
                        {invoice.payment_date && (
                          <span>Paid: {new Date(invoice.payment_date).toLocaleDateString()}</span>
                        )}
                        {invoice.job_card_id && (
                          <span>Job Card: {invoice.job_card_id}</span>
                        )}
                        {invoice.quotation_number && (
                          <span className="inline-flex items-center text-indigo-700 font-medium" data-testid={`invoice-linked-quotation-${invoice.id}`}>
                            🔗 Quotation: {invoice.quotation_number}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex space-x-2 ml-4">
                      {invoice.status === 'draft' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateInvoiceStatus(invoice.id, 'sent')}
                          className="text-blue-600 border-blue-600 hover:bg-blue-50"
                        >
                          Send
                        </Button>
                      )}
                      
                      {(() => {
                        // Single source of truth: balance_due (kept up-to-date by backend on both advance & final payments).
                        // Fall back to the manual calculation only if balance_due isn't set.
                        const rawBalance = invoice.balance_due !== undefined && invoice.balance_due !== null
                          ? invoice.balance_due
                          : (invoice.total_amount || 0) - (invoice.total_advance_received || 0) - (invoice.final_payment_amount || 0);
                        const outstanding = Math.max(0, rawBalance);
                        const hasOutstanding = outstanding > 0.01; // small float tolerance
                        if (effectiveStatus === 'paid' || !hasOutstanding) return null;
                        return (
                          <Button
                            size="sm"
                            onClick={() => {
                              setPaymentInvoice(invoice);
                              setIsPaymentModalOpen(true);
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid={`receive-payment-btn-${invoice.id}`}
                          >
                            💰 Receive Payment
                          </Button>
                        );
                      })()}
                      
                      {effectiveStatus !== 'paid' && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setShowAdvancePaymentDialog(true);
                          }}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          💳 Advance Payment
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewInvoice(invoice)}
                        className="text-blue-600 border-blue-600 hover:bg-blue-50"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditInvoice(invoice)}
                        className="text-gray-600 border-gray-600 hover:bg-gray-50"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteInvoice(invoice.id)}
                        className="text-red-600 border-red-600 hover:bg-red-50"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Summary */}
      {filteredInvoices.length > 0 && (
        <div className="mt-6 text-center text-sm text-gray-600">
          Showing {filteredInvoices.length} of {invoices.length} invoices
        </div>
      )}

      {/* Add Invoice Modal */}
      <Dialog open={isAddInvoiceModalOpen} onOpenChange={setIsAddInvoiceModalOpen}>
        <DialogContent className="max-w-6xl">
          <AddInvoiceForm 
            onInvoiceAdded={handleInvoiceAdded}
            customers={customers}
            vehicles={vehicles}
            jobCards={jobCards}
            parts={parts}
            services={services}
          />
        </DialogContent>
      </Dialog>

      {/* Create Invoice from Quotation Modal */}
      <Dialog open={showFromQuotationDialog} onOpenChange={setShowFromQuotationDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Invoice from Quotation</DialogTitle>
            <DialogDescription>
              Select a quotation to convert into an invoice. Any advance payments will be applied automatically (once).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2" data-testid="from-quotation-list">
            {quotations.filter(q => q && q.status !== 'rejected' && q.status !== 'cancelled').length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">No quotations available to convert.</p>
            ) : (
              quotations
                .filter(q => q && q.status !== 'rejected' && q.status !== 'cancelled')
                .map((q) => {
                  const isConverted = q.status === 'converted' || q.status === 'invoiced';
                  return (
                    <div
                      key={q.id}
                      className="flex items-center justify-between border rounded-lg p-3 hover:bg-gray-50"
                      data-testid={`from-quotation-row-${q.id}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">{q.quote_number}</span>
                          {isConverted && (
                            <Badge className="bg-indigo-100 text-indigo-800">Converted{q.invoice_number ? ` → ${q.invoice_number}` : ''}</Badge>
                          )}
                          {q.total_advance_received > 0 && (
                            <Badge className="bg-green-100 text-green-800">Advance ₹{Number(q.total_advance_received).toLocaleString('en-IN')}</Badge>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 truncate">
                          {q.customer_name} · {q.vehicle_info} · ₹{Number(q.total_amount || 0).toLocaleString('en-IN')}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white ml-3 shrink-0"
                        disabled={convertingQuotationId === q.id}
                        onClick={() => handleConvertQuotationToInvoice(q)}
                        data-testid={`from-quotation-convert-btn-${q.id}`}
                      >
                        {convertingQuotationId === q.id ? 'Converting...' : (isConverted ? 'Create Another' : 'Convert')}
                      </Button>
                    </div>
                  );
                })
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Modal */}
      <Dialog open={isEditInvoiceModalOpen} onOpenChange={setIsEditInvoiceModalOpen}>
        <DialogContent className="max-w-6xl">
          {editingInvoice && (
            <EditInvoiceForm 
              invoice={editingInvoice}
              onInvoiceUpdated={handleInvoiceUpdated}
              customers={customers}
              jobCards={jobCards}
              parts={parts}
              services={services}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* View Invoice Modal */}
      <Dialog open={isViewInvoiceModalOpen} onOpenChange={setIsViewInvoiceModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          {viewingInvoice && (
            <InvoiceView 
              invoice={viewingInvoice} 
              onClose={() => setIsViewInvoiceModalOpen(false)}
              customers={customers}
              vehicles={vehicles}
              jobCards={jobCards}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Processing Dialog */}
      <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Receive Payment</DialogTitle>
            <DialogDescription>
              Process payment for Invoice #{paymentInvoice?.invoice_number}
            </DialogDescription>
          </DialogHeader>
          {paymentInvoice && (
            <InvoicePaymentForm
              invoice={paymentInvoice}
              onPaymentProcessed={(updatedInvoice) => {
                setInvoices(prev => prev.map(inv => 
                  inv.id === updatedInvoice.id ? updatedInvoice : inv
                ));
                setIsPaymentModalOpen(false);
                setPaymentInvoice(null);
                toast.success('Payment processed successfully!');
              }}
              onCancel={() => {
                setIsPaymentModalOpen(false);
                setPaymentInvoice(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Advance Payment Dialog for Invoices */}
      <Dialog open={showAdvancePaymentDialog} onOpenChange={setShowAdvancePaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Advance Payment</DialogTitle>
            <DialogDescription>
              Record advance payment for {selectedInvoice?.invoice_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Payment Amount Options */}
            <div>
              <Label className="text-base font-medium">Payment Amount</Label>
              <div className="mt-2 space-y-3">
                {/* Fixed Amount Option */}
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="invoice-fixed-amount"
                    name="invoice-amount-type"
                    checked={!advancePaymentData.is_percentage}
                    onChange={() => setAdvancePaymentData(prev => ({ ...prev, is_percentage: false, percentage: '' }))}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="invoice-fixed-amount">Fixed Amount</Label>
                </div>
                {!advancePaymentData.is_percentage && (
                  <div className="ml-6">
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Enter amount (₹)"
                      value={advancePaymentData.amount}
                      onChange={(e) => setAdvancePaymentData(prev => ({ ...prev, amount: e.target.value }))}
                    />
                  </div>
                )}

                {/* Percentage Option */}
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="invoice-percentage-amount"
                    name="invoice-amount-type"
                    checked={advancePaymentData.is_percentage}
                    onChange={() => setAdvancePaymentData(prev => ({ ...prev, is_percentage: true, amount: '' }))}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="invoice-percentage-amount">Percentage of Total</Label>
                </div>
                {advancePaymentData.is_percentage && (
                  <div className="ml-6">
                    <div className="flex items-center space-x-2">
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        placeholder="Enter percentage"
                        value={advancePaymentData.percentage}
                        onChange={(e) => setAdvancePaymentData(prev => ({ ...prev, percentage: e.target.value }))}
                        className="flex-1"
                      />
                      <span className="text-sm text-gray-500">%</span>
                    </div>
                    {advancePaymentData.percentage && selectedInvoice && (
                      <p className="text-sm text-blue-600 mt-1">
                        Amount: ₹{((selectedInvoice.total_amount * parseFloat(advancePaymentData.percentage || 0)) / 100).toLocaleString('en-IN')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <Label htmlFor="invoice-payment-method">Payment Method</Label>
              <Select 
                value={advancePaymentData.payment_method} 
                onValueChange={(value) => setAdvancePaymentData(prev => ({ ...prev, payment_method: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="upi">UPI Payment</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bank Account Selection (for Bank Transfer) */}
            {advancePaymentData.payment_method === 'bank_transfer' && (
              <div>
                <Label htmlFor="invoice-bank-account">Select Bank Account *</Label>
                <Select 
                  value={advancePaymentData.bank_account_id} 
                  onValueChange={(value) => setAdvancePaymentData(prev => ({ ...prev, bank_account_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose bank account" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.bank_name} - {account.account_number.replace(/(.{4})/g, '$1 ').trim()}
                        <div className="text-xs text-gray-500">
                          Balance: ₹{(account.current_balance || 0).toLocaleString('en-IN')}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {advancePaymentData.payment_method === 'bank_transfer' && !advancePaymentData.bank_account_id && (
                  <p className="text-sm text-red-500 mt-1">Bank account selection is required</p>
                )}
              </div>
            )}

            {/* Reference Number (for Bank/UPI) */}
            {(advancePaymentData.payment_method === 'bank_transfer' || advancePaymentData.payment_method === 'upi') && (
              <div>
                <Label htmlFor="invoice-reference">Reference Number</Label>
                <Input
                  id="invoice-reference"
                  placeholder="Enter transaction reference number"
                  value={advancePaymentData.reference_number}
                  onChange={(e) => setAdvancePaymentData(prev => ({ ...prev, reference_number: e.target.value }))}
                />
              </div>
            )}

            {/* Notes */}
            <div>
              <Label htmlFor="invoice-notes">Notes (Optional)</Label>
              <Input
                id="invoice-notes"
                placeholder="Additional notes"
                value={advancePaymentData.notes}
                onChange={(e) => setAdvancePaymentData(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>

            {/* Payment Summary */}
            {selectedInvoice && (
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Invoice Total:</span>
                    <span>₹{selectedInvoice.total_amount?.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Already Received:</span>
                    <span>₹{(selectedInvoice.total_advance_received || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-blue-700 border-t border-blue-200 pt-1">
                    <span>Remaining Balance:</span>
                    <span>₹{(selectedInvoice.total_amount - (selectedInvoice.total_advance_received || 0)).toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowAdvancePaymentDialog(false);
                setAdvancePaymentData({
                  amount: '',
                  payment_method: 'cash',
                  reference_number: '',
                  notes: '',
                  percentage: '',
                  is_percentage: false
                });
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAdvancePayment}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Record Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { InvoicesSection };
